// src/ids.ts
import { z } from "zod";
var TYPEID_SUFFIX = "[0-9a-hjkmnp-tv-z]{26}";
function typeId(prefix, entity, example) {
  return z.string().regex(new RegExp(`^${prefix}_${TYPEID_SUFFIX}$`)).describe(
    `Identifiant opaque ${entity} : pr\xE9fixe \xAB ${prefix}_ \xBB suivi de 26 caract\xE8res en base32 minuscule (0-9a-hjkmnp-tv-z). Exemple : ${example}. \xC0 traiter comme une cha\xEEne opaque : ne pas l\u2019analyser, ne pas supposer d\u2019ordre.`
  );
}
var requestIdSchema = typeId("req", "d\u2019une requ\xEAte", "req_01m4c2adw2fzr821h1r0w12fr8");
var fileIdSchema = typeId("fil", "d\u2019un fichier", "fil_01m4c2h78qexsrxkp9y8k1rqf7");
var webhookDeliveryIdSchema = typeId(
  "whd",
  "d\u2019une livraison de webhook",
  "whd_01m4c2h7e0exsrxqhwrzqy6d39"
);
var useCaseIdSchema = typeId("uc", "d\u2019un cas d\u2019usage", "uc_01m4c2h7dzexsrxqcjy2yvktv6");
function timestampSchema(description) {
  return z.string().datetime().describe(`${description} Format ISO 8601 UTC avec millisecondes, suffixe Z (ex. 2026-06-15T12:00:00.000Z).`);
}

// src/errors.ts
import { z as z2 } from "zod";
var ERROR_CODES = [
  // HTTP / transport level
  "validation_error",
  "unauthorized",
  "forbidden",
  "not_found",
  "rate_limited",
  "internal_error",
  // conflicts
  "conflict",
  // request lifecycle (PRD 11)
  "idempotency_conflict",
  "not_cancellable",
  "already_finished",
  // rejection outcomes (PRD 9)
  "rejected_invalid_input",
  "rejected_use_case_unavailable",
  "rejected_user_unknown",
  "rejected_user_inactive",
  "rejected_cap_reached"
];
var errorCodeSchema = z2.enum(ERROR_CODES).describe(
  "Code d\u2019erreur machine (union ferm\xE9e de 15 valeurs). C\u2019est la SEULE valeur sur laquelle brancher la logique de reprise : plusieurs codes partagent le m\xEAme statut HTTP (422 : 6 codes ; 409 : 4 codes). Le catalogue complet, avec la conduite \xE0 tenir pour chaque code, figure dans la description de ce document."
);
var problemDocumentSchema = z2.object({
  type: z2.string().url().describe(
    "Identifiant URI du type d\u2019erreur, de la forme \xAB <URL publique de l\u2019API>/errors/<code> \xBB. C\u2019est un identifiant stable, pas une page de documentation : l\u2019URL n\u2019est pas r\xE9soluble (GET renvoie 404). Ne pas la suivre."
  ),
  title: z2.string().describe(
    "Titre court en fran\xE7ais, fixe pour un `code` donn\xE9 (ex. \xAB Plafond atteint \xBB). Ne pas s\u2019en servir pour d\xE9cider."
  ),
  status: z2.number().int().min(400).max(599).describe("Statut HTTP de la r\xE9ponse, r\xE9p\xE9t\xE9 dans le corps."),
  code: errorCodeSchema,
  detail: z2.string().nullable().describe(
    "Pr\xE9cision en fran\xE7ais, texte libre destin\xE9 \xE0 un humain ou \xE0 un journal ; sa formulation peut changer sans pr\xE9avis : ne jamais l\u2019analyser pour d\xE9cider. Vaut `null` quand aucune pr\xE9cision n\u2019existe (c\u2019est le cas des rejets m\xE9tier enregistr\xE9s, sauf un SMS refus\xE9, qui en donne le motif). Pour une erreur de validation, contient les chemins fautifs sous la forme \xAB chemin: message \xBB s\xE9par\xE9s par \xAB ; \xBB (messages en anglais)."
  ),
  requestId: z2.string().nullable().optional().describe(
    "Identifiant de corr\xE9lation HTTP de l\u2019appel (UUID, ou la valeur de l\u2019en-t\xEAte `X-Request-Id` que vous avez fournie si elle fait 128 caract\xE8res au plus). Ce n\u2019est PAS l\u2019identifiant `req_\u2026` d\u2019une requ\xEAte IA. \xC0 communiquer au support avec l\u2019heure de l\u2019appel."
  )
}).describe(
  "Document d\u2019erreur RFC 9457 (`application/problem+json`). Les six membres sont toujours pr\xE9sents dans les erreurs \xE9mises par l\u2019API."
);
var PROBLEM_CONTENT_TYPE = "application/problem+json";
var ERROR_CODE_STATUS = {
  validation_error: 422,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  rate_limited: 429,
  internal_error: 500,
  conflict: 409,
  idempotency_conflict: 409,
  not_cancellable: 409,
  already_finished: 409,
  rejected_invalid_input: 422,
  rejected_use_case_unavailable: 422,
  rejected_user_unknown: 422,
  rejected_user_inactive: 422,
  rejected_cap_reached: 422
};
var ERROR_TITLES = {
  validation_error: "Requ\xEAte invalide",
  unauthorized: "Authentification requise",
  forbidden: "Acc\xE8s refus\xE9",
  not_found: "Ressource introuvable",
  rate_limited: "Trop de requ\xEAtes",
  internal_error: "Erreur interne",
  conflict: "Conflit",
  idempotency_conflict: "Conflit de cl\xE9 d'idempotence",
  not_cancellable: "Requ\xEAte non annulable",
  already_finished: "Requ\xEAte d\xE9j\xE0 termin\xE9e",
  rejected_invalid_input: "Entr\xE9e invalide",
  rejected_use_case_unavailable: "Cas d'usage indisponible",
  rejected_user_unknown: "Utilisateur inconnu",
  rejected_user_inactive: "Utilisateur d\xE9sactiv\xE9",
  rejected_cap_reached: "Plafond atteint"
};
function errorTitle(code) {
  return ERROR_TITLES[code];
}

// src/money.ts
import { z as z3 } from "zod";
var euroAmountSchema = z3.string().regex(/^\d+(\.\d{1,6})?$/, "Montant d\xE9cimal en euros, jusqu'\xE0 6 d\xE9cimales").describe(
  "Montant en euros sous forme de cha\xEEne d\xE9cimale (jamais un nombre JSON) : partie enti\xE8re obligatoire, point comme s\xE9parateur d\xE9cimal, 0 \xE0 6 d\xE9cimales, sans s\xE9parateur de milliers ni signe. Valeurs valides : \xAB 0 \xBB, \xAB 0.75 \xBB, \xAB 1200.5 \xBB, \xAB 0.000001 \xBB. Valeur maximale accept\xE9e : \xAB 9007199254.740991 \xBB. Valeurs refus\xE9es (422 validation_error) : \xAB .5 \xBB, \xAB 0,75 \xBB, \xAB 1e3 \xBB, \xAB -1 \xBB, une cha\xEEne de plus de 6 d\xE9cimales, un nombre JSON, un montant sup\xE9rieur au maximum."
);
var moneySchema = z3.object({
  amount: z3.string().describe(
    "Montant en euros, cha\xEEne d\xE9cimale de 0 \xE0 6 d\xE9cimales, sans z\xE9ros de fin ni s\xE9parateur de milliers : \xAB 0 \xBB, \xAB 0.03 \xBB, \xAB 0.75 \xBB, \xAB 1200.5 \xBB. Ne pas convertir en nombre flottant pour calculer : utiliser une arithm\xE9tique d\xE9cimale."
  ),
  currency: z3.literal("EUR").describe("Devise ISO 4217. Toujours \xAB EUR \xBB.")
}).describe("Montant mon\xE9taire en euros.");

// src/admin.ts
import { z as z4 } from "zod";
var externalIdSchema = z4.string().min(1).max(255).describe(
  "Identifiant de l\u2019utilisateur dans VOTRE syst\xE8me : c\u2019est la valeur que vous envoyez ensuite dans l\u2019en-t\xEAte `X-Majuscule-User`. 1 \xE0 255 caract\xE8res, compar\xE9 tel quel (sensible \xE0 la casse, aucune normalisation), unique pour votre client. \xC9viter \xAB / \xBB, \xAB . \xBB et \xAB .. \xBB seuls et les caract\xE8res de contr\xF4le (dont U+0000) : ils ne sont pas adressables dans un chemin d\u2019URL ou provoquent une erreur serveur. Encoder en URL (percent-encoding) quand l\u2019identifiant figure dans un chemin."
);
var consumptionMeasure = "Mesure identique \xE0 celle des plafonds : montants BRUTS factur\xE9s des requ\xEAtes termin\xE9es dans le mois (Europe/Paris) + r\xE9servations des requ\xEAtes en cours (`queued`, `running`, `batched`), hors remises et ajustements de facturation.";
var createUserSchema = z4.object({
  externalId: externalIdSchema,
  displayName: z4.string().max(200).optional().describe("Optionnel : nom d\u2019affichage, 200 caract\xE8res au plus. Omis : `null` dans la ressource."),
  userCap: euroAmountSchema.nullable().optional().describe(
    "Optionnel : plafond mensuel de l\u2019utilisateur en euros. OMIS : l\u2019utilisateur re\xE7oit une copie du plafond utilisateur par d\xE9faut du client (valeur configur\xE9e par l\u2019op\xE9rateur, non lisible via l\u2019API). `null` EXPLICITE : aucun plafond personnel. Une valeur : ce plafond (0 bloque toute requ\xEAte). Ne peut pas d\xE9passer le plafond du client (422 validation_error)."
  ),
  canEditOwnCap: z4.boolean().describe(
    "OBLIGATOIRE. Vrai : l\u2019utilisateur peut modifier son propre plafond via `PATCH /v1/usage/me/cap` (sans d\xE9passer le plafond du client)."
  ),
  canViewAmounts: z4.boolean().describe(
    "OBLIGATOIRE. Vrai : l\u2019utilisateur voit les montants (`price` des requ\xEAtes, `spent` et `cap` de `GET /v1/usage/me`). Faux : il ne voit que des pourcentages (`percentUsed`) et la cl\xE9 `price` est absente des ressources qu\u2019il lit."
  )
}).describe("Corps de `POST /v1/admin/users`. Les champs inconnus sont ignor\xE9s en silence.");
var updateUserSchema = z4.object({
  displayName: z4.string().max(200).nullable().describe("Nouveau nom d\u2019affichage (200 caract\xE8res au plus), ou `null` pour l\u2019effacer."),
  active: z4.boolean().describe(
    "Faux : d\xE9sactive l\u2019utilisateur. Ses appels de service sont alors refus\xE9s en 422 `rejected_user_inactive` ; ses requ\xEAtes d\xE9j\xE0 accept\xE9es ne sont PAS annul\xE9es. Vrai : le r\xE9active."
  ),
  userCap: euroAmountSchema.nullable().describe(
    "Nouveau plafond mensuel en euros, ou `null` pour supprimer son plafond personnel. Ne peut pas d\xE9passer le plafond du client (422 validation_error)."
  ),
  canEditOwnCap: z4.boolean().describe("Autoriser ou non l\u2019utilisateur \xE0 modifier son propre plafond."),
  canViewAmounts: z4.boolean().describe("Autoriser ou non l\u2019utilisateur \xE0 voir les montants.")
}).partial().describe(
  "Corps de `PATCH /v1/admin/users/{externalId}`. Tous les champs sont optionnels : un champ ABSENT reste inchang\xE9 ; `null` efface seulement `displayName` et `userCap`. `externalId` n\u2019est pas modifiable. Un corps `{}` est accept\xE9 et ne change rien."
);
var updateClientCapSchema = z4.object({
  clientCap: euroAmountSchema.nullable().describe(
    "Nouveau plafond mensuel du client, en euros (cha\xEEne d\xE9cimale), ou `null` pour le supprimer. La cl\xE9 est OBLIGATOIRE. Abaisser le plafond sous les plafonds personnels existants ne les corrige pas : les utilisateurs concern\xE9s restent configur\xE9s au-dessus du plafond du client (leurs nouvelles requ\xEAtes sont alors rejet\xE9es d\xE8s que le plafond du client est atteint)."
  )
}).describe("Corps de `PATCH /v1/admin/cap`.");
var userResourceSchema = z4.object({
  externalId: z4.string().describe("Identifiant de l\u2019utilisateur dans votre syst\xE8me."),
  displayName: z4.string().nullable().describe("Nom d\u2019affichage, ou `null`."),
  active: z4.boolean().describe("Faux : l\u2019utilisateur est d\xE9sactiv\xE9 (ses appels de service sont refus\xE9s)."),
  userCap: moneySchema.nullable().describe("Plafond mensuel personnel de l\u2019utilisateur, ou `null` s\u2019il n\u2019en a pas."),
  canEditOwnCap: z4.boolean().describe("Peut modifier son propre plafond."),
  canViewAmounts: z4.boolean().describe("Voit les montants (sinon seulement des pourcentages)."),
  usage: z4.object({
    month: z4.string().describe("Mois courant YYYY-MM (Europe/Paris)."),
    spent: moneySchema.describe(
      `Consommation de l\u2019utilisateur sur le mois courant (m\xEAme valeur que \`spent\` de \`GET /v1/usage/me\` pour cet utilisateur). ${consumptionMeasure} Vaut \xAB 0 \xBB sans consommation.`
    ),
    percentUsed: z4.number().nullable().describe(
      "Pourcentage du plafond consomm\xE9 = consommation de l\u2019UTILISATEUR / plafond applicable \xD7 100, arrondi \xE0 une d\xE9cimale, NON plafonn\xE9 \xE0 100 (peut d\xE9passer 100). Le plafond applicable est le plafond personnel de l\u2019utilisateur, \xE0 d\xE9faut celui du client. `null` si aucun plafond ne s\u2019applique (ou si le plafond applicable vaut 0)."
    )
  }).describe(
    "Consommation de l\u2019utilisateur sur le mois en cours. Les routes d\u2019administration montrent toujours les montants, quel que soit `canViewAmounts` de l\u2019utilisateur."
  )
}).describe("Utilisateur du client.");
var userListSchema = z4.object({
  users: z4.array(userResourceSchema).describe("Tous les utilisateurs du client, sans pagination ni ordre garanti.")
}).describe("Liste des utilisateurs du client.");
var clientCapResourceSchema = z4.object({
  month: z4.string().describe("Mois courant YYYY-MM (Europe/Paris)."),
  clientCap: moneySchema.nullable().describe("Plafond mensuel du client, ou `null` s\u2019il n\u2019y en a pas. Le plafond de s\xE9curit\xE9 n\u2019est jamais expos\xE9."),
  spent: moneySchema.describe(
    `Consommation du client sur le mois courant, tous utilisateurs confondus : la valeur compar\xE9e \xE0 \`clientCap\` pour accepter une nouvelle requ\xEAte (avec la r\xE9servation de celle-ci). ${consumptionMeasure} Vaut \xAB 0 \xBB sans consommation.`
  )
}).describe("Plafond du client et consommation du mois.");
var adminUsageResourceSchema = z4.object({
  month: z4.string().describe("Mois courant YYYY-MM (Europe/Paris)."),
  byUser: z4.array(
    z4.object({
      externalId: z4.string().describe("Identifiant de l\u2019utilisateur."),
      spent: moneySchema.describe("Consommation du mois de cet utilisateur (m\xEAme valeur que `usage.spent` de `GET /v1/admin/users`).")
    })
  ).describe(
    "Consommation du mois par utilisateur (identifi\xE9 par son `externalId`), de la plus \xE9lev\xE9e \xE0 la plus faible (\xE0 \xE9galit\xE9 : ordre alphab\xE9tique de `externalId`). Seuls les utilisateurs ayant une consommation non nulle ce mois-ci apparaissent : tableau vide si rien n\u2019a \xE9t\xE9 consomm\xE9 ; pour lister tous les utilisateurs, utiliser `GET /v1/admin/users`."
  ),
  byUseCase: z4.array(
    z4.object({
      useCase: z4.string().describe("Cl\xE9 du cas d\u2019usage."),
      spent: moneySchema.describe("Consommation du mois sur ce cas d\u2019usage, tous utilisateurs du client confondus.")
    })
  ).describe(
    "Consommation du mois par cas d\u2019usage (cl\xE9 du cas d\u2019usage), de la plus \xE9lev\xE9e \xE0 la plus faible (\xE0 \xE9galit\xE9 : ordre alphab\xE9tique de la cl\xE9). Seuls les cas d\u2019usage ayant une consommation non nulle apparaissent. Peut diff\xE9rer l\xE9g\xE8rement de la r\xE9partition par utilisateur pour des requ\xEAtes en cours \xE0 cheval sur deux mois."
  )
}).describe(
  `Consommation du mois courant du client, r\xE9partie par utilisateur et par cas d\u2019usage. ${consumptionMeasure}`
);

// src/clients.ts
import { z as z5 } from "zod";
var billingAddressSchema = z5.object({
  street: z5.string().trim().min(1).max(250),
  zipCode: z5.string().trim().min(1).max(20),
  city: z5.string().trim().min(1).max(50),
  countryCode: z5.string().regex(/^[A-Z]{2}$/, "Code pays ISO \xE0 2 lettres, ex. FR").default("FR")
});
var sirenSchema = z5.string().regex(/^\d{9}$/, "SIREN : 9 chiffres");
var billingDetailsShape = {
  legalName: z5.string().trim().min(1).max(200),
  siren: sirenSchema,
  vatNumber: z5.string().trim().max(20).nullable().optional(),
  billingEmail: z5.string().email(),
  billingAddress: billingAddressSchema.nullable().optional()
};
var createClientBillingSchema = z5.discriminatedUnion("source", [
  z5.object({ source: z5.literal("qonto"), qontoClientId: z5.string().min(1).max(100) }),
  z5.object({ source: z5.literal("new"), ...billingDetailsShape })
]);
var capsShape = {
  safetyCap: euroAmountSchema,
  clientCap: euroAmountSchema.nullable().optional(),
  defaultUserCap: euroAmountSchema.nullable().optional(),
  alertThresholds: z5.array(z5.number().int().positive().max(200)).optional(),
  maxConcurrentRequests: z5.number().int().min(1).max(100).optional()
};
var createClientSchema = z5.object({
  name: z5.string().min(1).max(200),
  billing: createClientBillingSchema,
  ...capsShape
});
var updateClientSchema = z5.object({ name: z5.string().min(1).max(200), ...billingDetailsShape, ...capsShape }).partial();
var linkQontoClientSchema = z5.object({ qontoClientId: z5.string().min(1).max(100) });
var qontoClientResourceSchema = z5.object({
  id: z5.string(),
  legalName: z5.string().nullable(),
  siren: z5.string().nullable(),
  vatNumber: z5.string().nullable(),
  email: z5.string().nullable(),
  billingAddress: billingAddressSchema.nullable(),
  /** Details Qonto lacks to link this client (« raison sociale », « SIREN », « email »). */
  missing: z5.array(z5.string()),
  /** The gateway client already linked to it, if any. */
  linkedClientId: z5.string().nullable()
});
var apiKeyKindSchema = z5.enum(["service", "admin"]);
var environmentSchema = z5.enum(["production", "staging"]);
var createApiKeySchema = z5.object({
  kind: apiKeyKindSchema,
  environment: environmentSchema.default("production"),
  label: z5.string().max(120).optional()
});
var createdApiKeyResourceSchema = z5.object({
  id: z5.string(),
  kind: apiKeyKindSchema,
  environment: environmentSchema,
  prefix: z5.string(),
  key: z5.string(),
  label: z5.string().nullable(),
  createdAt: z5.string()
});
var apiKeyResourceSchema = z5.object({
  id: z5.string(),
  kind: apiKeyKindSchema,
  environment: environmentSchema,
  prefix: z5.string(),
  label: z5.string().nullable(),
  lastUsedAt: z5.string().nullable(),
  revokedAt: z5.string().nullable(),
  createdAt: z5.string()
});
var setWebhookSchema = z5.object({
  url: z5.string().url().startsWith("https://", "HTTPS obligatoire")
});
var clientResourceSchema = z5.object({
  id: z5.string(),
  name: z5.string(),
  legalName: z5.string(),
  siren: z5.string().nullable(),
  vatNumber: z5.string().nullable(),
  billingEmail: z5.string(),
  billingAddress: z5.object({ street: z5.string(), zipCode: z5.string(), city: z5.string(), countryCode: z5.string() }).nullable(),
  status: z5.enum(["active", "paused", "disabled"]),
  pauseReason: z5.enum(["safety_cap", "platform_cap", "manual"]).nullable(),
  clientCap: z5.string().nullable(),
  safetyCap: z5.string(),
  defaultUserCap: z5.string().nullable(),
  alertThresholds: z5.array(z5.number()),
  maxConcurrentRequests: z5.number(),
  webhookUrl: z5.string().nullable(),
  /** Webhook of the staging ERP, which receives the events of staging requests. */
  stagingWebhookUrl: z5.string().nullable(),
  sepaMandateStatus: z5.enum(["none", "active", "inactive"]),
  /** The client's record in Qonto, which holds its billing details (D-QONTO.8). */
  qontoClientId: z5.string().nullable(),
  /** Billing deferred: every close carries the month to the next one (D-BILL.2). */
  billingDeferred: z5.boolean()
});
var capScopeSchema = z5.enum(["user", "client", "safety", "platform"]);
var capChangeActorTypeSchema = z5.enum(["operator", "admin_key", "end_user"]);
var capChangeResourceSchema = z5.object({
  id: z5.string(),
  scope: capScopeSchema,
  /** External id of the user concerned, resolved for display; null otherwise. */
  endUserExternalId: z5.string().nullable(),
  /** Previous cap as an exact euro string, or null for "no cap". */
  previousCap: z5.string().nullable(),
  /** New cap as an exact euro string, or null for "no cap". */
  newCap: z5.string().nullable(),
  actorType: capChangeActorTypeSchema,
  actorLabel: z5.string(),
  createdAt: z5.string()
});

// src/charges.ts
import { z as z6 } from "zod";
var chargeFrequencySchema = z6.enum(["monthly", "annual"]);
var monthSchema = z6.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Format attendu : YYYY-MM.");
var createChargeSchema = z6.object({
  label: z6.string().min(1).max(200),
  frequency: chargeFrequencySchema,
  /** Amount billed to the client for the frequency (EUR string). */
  billed: euroAmountSchema,
  /** Real cost to the operator for the frequency (EUR string, defaults to 0). */
  cost: euroAmountSchema.default("0"),
  startMonth: monthSchema,
  endMonth: monthSchema.nullable().optional()
});
var updateChargeSchema = createChargeSchema.partial();
var chargeResourceSchema = z6.object({
  id: z6.string(),
  clientId: z6.string(),
  label: z6.string(),
  frequency: chargeFrequencySchema,
  /** Amount for the frequency (what the client pays per month or per year). */
  billed: z6.string(),
  cost: z6.string(),
  /** Smoothed to the month (annual → 1/12). */
  monthlyBilled: z6.string(),
  monthlyCost: z6.string(),
  monthlyMargin: z6.string(),
  startMonth: z6.string(),
  endMonth: z6.string().nullable(),
  /** Whether the charge is billable in the current month. */
  active: z6.boolean()
});

// src/platform-charges.ts
import { z as z7 } from "zod";
var platformChargeFrequencySchema = z7.enum(["monthly", "annual"]);
var monthSchema2 = z7.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Format attendu : YYYY-MM.");
var createPlatformChargeSchema = z7.object({
  label: z7.string().min(1).max(200),
  frequency: platformChargeFrequencySchema,
  /** What this overhead costs the operator for the frequency (EUR string). */
  cost: euroAmountSchema,
  startMonth: monthSchema2,
  endMonth: monthSchema2.nullable().optional()
});
var updatePlatformChargeSchema = createPlatformChargeSchema.partial();
var platformChargeResourceSchema = z7.object({
  id: z7.string(),
  label: z7.string(),
  frequency: platformChargeFrequencySchema,
  cost: z7.string(),
  /** Smoothed to the month (annual → 1/12). */
  monthlyCost: z7.string(),
  startMonth: z7.string(),
  endMonth: z7.string().nullable(),
  active: z7.boolean()
});

// src/catalog.ts
import { z as z8 } from "zod";
var decimalString = z8.string().regex(/^\d+(\.\d+)?$/, "Nombre d\xE9cimal");
var providerSchema = z8.enum(["anthropic", "google", "vonage"]);
var modelFeatureSchema = z8.enum([
  "text",
  "pdf_input",
  "image_input",
  "image_output",
  "structured_output",
  "batch",
  "sms"
]);
var tariffCurrencySchema = z8.enum(["USD", "EUR"]);
var createModelSchema = z8.object({
  provider: providerSchema,
  providerModelId: z8.string().min(1),
  displayName: z8.string().min(1),
  features: z8.array(modelFeatureSchema),
  /** Omitted: the provider's (USD for the AI providers, EUR for Vonage). */
  currency: tariffCurrencySchema.optional()
});
var updateModelSchema = z8.object({
  features: z8.array(modelFeatureSchema)
});
var createTariffSchema = z8.object({
  mode: z8.enum(["standard", "batch"]),
  effectiveFrom: z8.string().datetime(),
  currency: tariffCurrencySchema.optional(),
  inputPerMTok: decimalString,
  outputPerMTok: decimalString,
  cacheWritePerMTok: decimalString,
  cacheReadPerMTok: decimalString,
  perOutputImage: decimalString,
  /** Price per SMS segment sent: a Vonage SMS service; '0' for an AI model. */
  perMessage: decimalString.default("0")
});
var createExchangeRateSchema = z8.object({
  date: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  usdToEur: decimalString
});
var useCaseKindSchema = z8.enum(["text", "image", "sms"]);
var useCaseCategorySchema = z8.enum(["ai", "communication"]);
var smsSettingsSchema = z8.object({
  sender: z8.string().min(1).max(32),
  allowedCallingCodes: z8.array(z8.string().min(1).max(8)).min(1).max(20)
});
var invoiceNameSchema = z8.string().trim().min(1).max(40).nullable();
var invoiceDescriptionSchema = z8.string().trim().min(1).max(300).nullable();
var createAiUseCaseSchema = z8.object({
  key: z8.string().min(1).max(100),
  kind: z8.enum(["text", "image"]),
  acceptsFiles: z8.boolean().default(false),
  ownerClientId: z8.string().min(1),
  modelId: z8.string().min(1),
  params: z8.object({
    maxOutputTokens: z8.number().int().positive().nullable(),
    temperature: z8.number().min(0).max(2).nullable()
  }),
  executionMode: z8.enum(["realtime", "batch"]),
  invoiceName: invoiceNameSchema.optional(),
  invoiceDescription: invoiceDescriptionSchema.optional()
});
var createSmsUseCaseSchema = z8.object({
  key: z8.string().min(1).max(100),
  kind: z8.literal("sms"),
  ownerClientId: z8.string().min(1),
  modelId: z8.string().min(1),
  sms: smsSettingsSchema,
  invoiceName: invoiceNameSchema.optional(),
  invoiceDescription: invoiceDescriptionSchema.optional()
});
var createUseCaseSchema = z8.discriminatedUnion("kind", [
  createAiUseCaseSchema,
  createSmsUseCaseSchema
]);
var duplicateUseCaseSchema = z8.object({
  targetClientId: z8.string().min(1).describe("Client qui recevra la copie."),
  key: z8.string().min(1).max(100).optional().describe("Cl\xE9 de la copie. Par d\xE9faut, la cl\xE9 du cas d\u2019usage source."),
  sms: smsSettingsSchema.optional().describe(
    "R\xE9glages SMS de la copie, pour un cas d\u2019usage SMS uniquement. Par d\xE9faut, ceux de la source ; l\u2019exp\xE9diteur est en g\xE9n\xE9ral la marque du client cible."
  )
});
var pricingUnitSchema = z8.enum(["page", "image", "sms"]);
var pricingRuleBodySchema = z8.discriminatedUnion("type", [
  z8.object({ type: z8.literal("multiplier"), coefficient: decimalString }),
  z8.object({ type: z8.literal("fixed_per_request"), amount: euroAmountSchema }),
  z8.object({
    type: z8.literal("per_unit"),
    unit: pricingUnitSchema,
    amount: euroAmountSchema
  })
]);
var createPricingRuleSchema = z8.object({
  clientId: z8.string().nullable().optional(),
  effectiveFrom: z8.string().datetime(),
  rule: pricingRuleBodySchema
});
var updateUseCaseSchema = z8.object({
  active: z8.boolean().optional(),
  sms: smsSettingsSchema.optional(),
  invoiceName: invoiceNameSchema.optional(),
  invoiceDescription: invoiceDescriptionSchema.optional()
}).refine(
  (body) => body.active !== void 0 || body.sms !== void 0 || body.invoiceName !== void 0 || body.invoiceDescription !== void 0,
  { message: "Au moins un champ attendu : active, sms, invoiceName ou invoiceDescription." }
);

// src/requests.ts
import { z as z9 } from "zod";
var jsonSchemaSchema = z9.record(z9.unknown()).describe(
  "Sch\xE9ma JSON (un objet) auquel la sortie du mod\xE8le doit se conformer. La passerelle valide la sortie du mod\xE8le selon JSON Schema draft-07 (`format` n\u2019est pas appliqu\xE9 ; les mots-cl\xE9s 2019-09/2020-12 comme `prefixItems` ou `unevaluatedProperties` sont ignor\xE9s ; les `$ref` internes vers `$defs` ou `definitions` sont accept\xE9s). Ne pas fournir `$id`, ne fournir `$schema` qu\u2019en `http://json-schema.org/draft-07/schema#` (ou l\u2019omettre, ce qui est recommand\xE9), ne pas utiliser de `$ref` externe ni d\u2019expression r\xE9guli\xE8re invalide. ATTENTION : le sch\xE9ma lui-m\xEAme n\u2019est PAS valid\xE9 \xE0 la soumission (seul le type \xAB objet \xBB est exig\xE9) ; un sch\xE9ma non compilable (type inconnu, `$ref` non r\xE9soluble, expression r\xE9guli\xE8re invalide, `$schema` autre que draft-07, `$id` d\xE9j\xE0 utilis\xE9) est accept\xE9 (202) puis emp\xEAche le traitement : apr\xE8s l\u2019appel au fournisseur, la requ\xEAte reste ind\xE9finiment en `running`, sans statut terminal ni webhook, avec sa r\xE9servation de budget, et n\u2019est alors pas annulable (409 `not_cancellable`) : communiquer son `id` au support. Valider le sch\xE9ma de votre c\xF4t\xE9 avant l\u2019envoi."
);
var textPartSchema = z9.object({
  type: z9.literal("text").describe("Discriminant : bloc de texte."),
  text: z9.string().describe("Texte du bloc. La cha\xEEne vide est accept\xE9e."),
  cache: z9.boolean().optional().describe(
    "Optionnel : `true` demande un point de cache de prompt (\xAB prompt caching \xBB) chez le fournisseur apr\xE8s ce bloc. N\u2019a d\u2019effet que si le mod\xE8le du cas d\u2019usage est servi par Anthropic ; ignor\xE9 avec Google. Placer les parties stables du prompt en premier. \xC0 omettre plut\xF4t que d\u2019envoyer `null`."
  )
}).describe("Bloc de texte d\u2019un message.");
var filePartSchema = z9.object({
  type: z9.literal("file").describe("Discriminant : r\xE9f\xE9rence \xE0 un fichier."),
  fileId: z9.string().describe(
    "Identifiant `fil_\u2026` d\u2019un fichier d\xE9pos\xE9 au pr\xE9alable via `POST /v1/files` (PDF, PNG, JPEG ou WEBP). Un identifiant mal form\xE9 est refus\xE9 en 422 `rejected_invalid_input` (\xAB Entr\xE9e invalide. \xBB) ; un fichier inconnu, supprim\xE9 (conserv\xE9s 7 jours) ou appartenant \xE0 un autre client donne un rejet enregistr\xE9 (422 `rejected_invalid_input`, `detail` null). R\xE9serv\xE9 aux cas d\u2019usage qui acceptent les fichiers."
  )
}).describe("R\xE9f\xE9rence \xE0 un fichier d\xE9j\xE0 d\xE9pos\xE9, jointe au message.");
var textContentPart = z9.discriminatedUnion("type", [textPartSchema, filePartSchema]).describe("Bloc de contenu d\u2019un message : texte (`type: text`) ou fichier (`type: file`).");
var textMessage = z9.object({
  role: z9.enum(["user", "assistant"]).describe(
    "Auteur du message. Aucune r\xE8gle d\u2019alternance ou d\u2019ordre n\u2019est v\xE9rifi\xE9e par la passerelle ; un encha\xEEnement invalide pour le fournisseur donne l\u2019issue `provider_rejected`. Commencer par `user`."
  ),
  content: z9.array(textContentPart).min(1).describe("Blocs du message, dans l\u2019ordre (au moins un). Pas de maximum.")
}).describe("Un message de la conversation.");
var textRequestInputSchema = z9.object({
  kind: z9.literal("text").describe("Discriminant : g\xE9n\xE9ration de texte (\xE9ventuellement sur fichiers joints, \xE9ventuellement en sortie JSON)."),
  system: z9.object({
    text: z9.string().describe("Consigne syst\xE8me. La cha\xEEne vide est accept\xE9e."),
    cache: z9.boolean().optional().describe("Optionnel : point de cache apr\xE8s la consigne (Anthropic uniquement).")
  }).optional().describe(
    "Optionnel : consigne syst\xE8me. ATTENTION : elle n\u2019est transmise au mod\xE8le que lorsque le cas d\u2019usage est servi par Anthropic ; avec Google elle est ignor\xE9e en silence. Le fournisseur d\u2019un cas d\u2019usage n\u2019est pas expos\xE9 par l\u2019API : r\xE9p\xE9ter les consignes essentielles dans le premier message `user`. \xC0 omettre plut\xF4t que d\u2019envoyer `null`."
  ),
  messages: z9.array(textMessage).min(1).describe("Conversation \xE0 compl\xE9ter, au moins un message, pas de maximum."),
  responseFormat: z9.discriminatedUnion("type", [
    z9.object({ type: z9.literal("text") }),
    z9.object({ type: z9.literal("json_schema"), schema: jsonSchemaSchema })
  ]).optional().describe(
    'Optionnel : format de sortie. `{ "type": "text" }` : sans effet (texte libre). `{ "type": "json_schema", "schema": {\u2026} }` : sortie JSON ; la passerelle valide la sortie du mod\xE8le contre le sch\xE9ma \u2014 conforme : `result.json` contient l\u2019objet et l\u2019issue est `succeeded` ; non conforme ou non analysable : issue `invalid_output` (FACTUR\xC9E, jamais r\xE9essay\xE9e). Le sch\xE9ma contraint aussi la g\xE9n\xE9ration (sortie JSON native d\u2019Anthropic et de Google). Les mots-cl\xE9s que le fournisseur ne sait pas imposer (ex. `maxItems`, `minLength`, `pattern`, `minimum`) lui sont transmis comme simple indication : la passerelle les v\xE9rifie \xE0 la validation. Un sch\xE9ma que le fournisseur refuse (r\xE9cursif, trop complexe) donne l\u2019issue `provider_rejected`, non factur\xE9e. \xC0 omettre plut\xF4t que d\u2019envoyer `null`.'
  ),
  maxOutputTokens: z9.number().int().positive().optional().describe(
    "Optionnel : nombre maximal de tokens en sortie (entier > 0, sans maximum d\xE9clar\xE9). Sert \xE0 ABAISSER la limite configur\xE9e pour le cas d\u2019usage (4096 par d\xE9faut) : la r\xE9servation de budget retient la plus petite des deux valeurs, donc ne pas s\u2019en servir pour la relever. Avec Anthropic, la valeur est transmise telle quelle au mod\xE8le ; avec Google, elle est ignor\xE9e (la limite du cas d\u2019usage s\u2019applique). Atteindre la limite donne l\u2019issue `output_truncated` (FACTUR\xC9E, `result.text` partiel). \xC0 omettre plut\xF4t que d\u2019envoyer `null`."
  )
}).describe(
  "Entr\xE9e de kind `text` : conversation texte, avec fichiers joints (PDF/images) si le cas d\u2019usage les accepte, et sortie JSON structur\xE9e optionnelle. \xC0 utiliser avec un cas d\u2019usage de type texte. C\u2019est aussi la forme \xE0 employer pour l\u2019extraction de documents (fichier + `responseFormat.json_schema`)."
);
var imageRequestInputSchema = z9.object({
  kind: z9.literal("image").describe("Discriminant : g\xE9n\xE9ration d\u2019image."),
  prompt: z9.string().describe("Description de l\u2019image \xE0 g\xE9n\xE9rer. La cha\xEEne vide est accept\xE9e."),
  inputFileIds: z9.array(z9.string()).optional().describe(
    "Optionnel : identifiants `fil_\u2026` d\u2019images de r\xE9f\xE9rence (d\xE9pos\xE9es via `POST /v1/files`), envoy\xE9es au mod\xE8le avant le `prompt` pour retoucher ou composer. Chaque fichier doit exister, appartenir au client et \xEAtre une image (PNG, JPEG, WebP), sinon la requ\xEAte est rejet\xE9e (`rejected_invalid_input`). Le nombre de r\xE9f\xE9rences ne change pas le prix."
  ),
  aspectRatio: z9.enum(["1:1", "3:4", "4:3", "9:16", "16:9"]).optional().describe(
    "Optionnel : ratio d\u2019image souhait\xE9. Accept\xE9 mais SANS EFFET \xE0 ce jour. Toute autre valeur est refus\xE9e (422 validation_error)."
  )
}).describe(
  "Entr\xE9e de kind `image` : g\xE9n\xE9ration d\u2019image. Exige un cas d\u2019usage de type image, qui ne peut pas \xEAtre en mode batch. Le r\xE9sultat porte les identifiants des images (`result.fileIds`), \xE0 t\xE9l\xE9charger via `GET /v1/files/{id}/content`. Le prix suit la r\xE8gle de tarification de votre client pour ce cas d\u2019usage (une g\xE9n\xE9ration compte pour une seule unit\xE9)."
);
var E164_PATTERN = /^\+[1-9]\d{6,14}$/;
var NO_CONTROL_CHARACTERS = /^[^\u0000-\u0009\u000B\u000C\u000E-\u001F\u007F]*$/;
var LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:^|[^\uD800-\uDBFF])[\uDC00-\uDFFF]/;
function isWellFormedUtf16(text2) {
  return !LONE_SURROGATE.test(text2);
}
var smsRequestInputSchema = z9.object({
  kind: z9.literal("sms").describe("Discriminant : envoi d\u2019un SMS."),
  to: z9.string().regex(E164_PATTERN, "Expected an E.164 phone number with a leading +, e.g. +33639980042").describe(
    "Num\xE9ro du destinataire au format E.164 : \xAB + \xBB, indicatif pays, puis num\xE9ro national sans le 0 initial, sans espace ni s\xE9parateur, 8 \xE0 16 caract\xE8res en tout (ex. \xAB +33639980042 \xBB pour le 06 39 98 00 42 en France). Un num\xE9ro mal form\xE9 (sans \xAB + \xBB, avec des espaces, \xAB 0033\u2026 \xBB, \xAB 06\u2026 \xBB) est refus\xE9 en 422 `validation_error`. Le pays doit figurer parmi les indicatifs autoris\xE9s du cas d\u2019usage (par d\xE9faut 33, France m\xE9tropolitaine) : sinon rejet ENREGISTR\xC9 (422 `rejected_invalid_input`, `detail` explicite), qui consomme la cl\xE9 d\u2019idempotence."
  ),
  text: z9.string().min(1).max(1600).regex(NO_CONTROL_CHARACTERS, "Control characters are not allowed (only line breaks)").refine(isWellFormedUtf16, "Invalid Unicode (lone surrogate)").describe(
    "Texte du SMS : 1 \xE0 1600 caract\xE8res (unit\xE9s UTF-16), sans caract\xE8re de contr\xF4le hormis les sauts de ligne (LF, CR ; CRLF compte comme LF), sans Unicode invalide. Factur\xE9 au SMS (segment). En GSM-7, l\u2019alphabet SMS standard, un SMS porte 160 caract\xE8res ; au-del\xE0 le message est d\xE9coup\xE9 en SMS de 153 caract\xE8res ; \u20AC [ ] { } ~ ^ | et l\u2019antislash comptent double. Un seul caract\xE8re hors GSM-7 fait passer tout le message en Unicode (UCS-2) : 70 caract\xE8res pour un SMS seul, puis 67 par SMS. C\u2019est le cas des lettres fran\xE7aises \xE7 \xEA \xE2 \xEE \xF4 \xFB \xEB \xEF \u0153 (\xE9 \xE8 \xE0 \xF9 \xC7 restent en GSM-7) et des emoji (2 unit\xE9s chacun). Les apostrophes et guillemets typographiques (\u2019 \xAB \xBB), les tirets (\u2013 \u2014), les points de suspension (\u2026) et les espaces ins\xE9cables sont convertis automatiquement en leurs \xE9quivalents GSM-7 quand cela garde tout le message en GSM-7 ; les lettres accentu\xE9es ne sont jamais modifi\xE9es. 6 SMS au plus (918 caract\xE8res GSM-7, 402 en Unicode) : au-del\xE0, ou sans caract\xE8re visible (espaces, sauts de ligne ou caract\xE8res invisibles comme U+200B seulement), rejet ENREGISTR\xC9 (422 `rejected_invalid_input`, `detail` explicite). L\u2019exp\xE9diteur affich\xE9 vient du cas d\u2019usage : il ne se choisit pas par requ\xEAte."
  )
}).describe(
  "Entr\xE9e de kind `sms` : envoi d\u2019un SMS \xE0 un destinataire. Exige un cas d\u2019usage de type SMS (temps r\xE9el, sans fichier), dont l\u2019exp\xE9diteur (nom affich\xE9) et les pays autoris\xE9s sont configur\xE9s par l\u2019op\xE9rateur. Le prix est par SMS (segment) envoy\xE9. `succeeded` signifie que l\u2019op\xE9rateur SMS a accept\xE9 le message pour acheminement, pas qu\u2019il a \xE9t\xE9 re\xE7u. Il n\u2019existe pas de champ `sender` ni `from` : un champ inconnu est ignor\xE9 en silence."
);
var requestInputSchema = z9.discriminatedUnion("kind", [
  textRequestInputSchema,
  imageRequestInputSchema,
  smsRequestInputSchema
]).describe(
  "Entr\xE9e de la requ\xEAte, discrimin\xE9e par `kind` (`text`, `image`, `sms`). Le `kind` doit correspondre au type du cas d\u2019usage cibl\xE9 : sinon rejet enregistr\xE9 422 `rejected_invalid_input` (`detail` null). Les champs inconnus sont ignor\xE9s en silence ; un champ optionnel doit \xEAtre OMIS, jamais envoy\xE9 \xE0 `null` (422)."
);
var createRequestBodyShape = {
  useCase: z9.string().min(1).describe(
    "Cl\xE9 du cas d\u2019usage \xE0 ex\xE9cuter (ex. \xAB facture.extraction \xBB), configur\xE9e par l\u2019op\xE9rateur pour votre client. Sensible \xE0 la casse. Une cl\xE9 inconnue, inactive ou appartenant \xE0 un autre client donne 422 `rejected_use_case_unavailable`. Il n\u2019existe pas d\u2019endpoint pour lister les cas d\u2019usage ni pour les d\xE9crire : obtenir aupr\xE8s de l\u2019op\xE9rateur la cl\xE9, le type attendu (`kind` de l\u2019entr\xE9e : `text`, `image` ou `sms`), si les fichiers sont accept\xE9s, le mode d\u2019ex\xE9cution (temps r\xE9el ou lot) et la limite de sortie ; pour un cas d\u2019usage SMS, l\u2019exp\xE9diteur affich\xE9 et les pays autoris\xE9s. Un `kind` incoh\xE9rent avec le cas d\u2019usage donne un rejet ENREGISTR\xC9 (422 `rejected_invalid_input`, `detail` null) qui consomme la cl\xE9 d\u2019idempotence."
  ),
  input: requestInputSchema,
  reference: z9.string().max(200).optional().describe(
    "Optionnel : votre r\xE9f\xE9rence m\xE9tier (identifiant de facture, de dossier\u2026), 200 caract\xE8res au plus. Renvoy\xE9e telle quelle dans la ressource et les webhooks ; filtre exact de `GET /v1/requests?reference=`. \xC0 omettre plut\xF4t que d\u2019envoyer `null`."
  ),
  metadata: z9.record(z9.string()).refine((m) => Object.keys(m).length <= 20).optional().describe(
    "Optionnel : dictionnaire cha\xEEne \u2192 cha\xEEne, 20 cl\xE9s au plus, renvoy\xE9 tel quel dans la ressource (`{}` si absent). Aucune borne sur la longueur des cl\xE9s et valeurs. \xC0 omettre plut\xF4t que d\u2019envoyer `null`. Attention : l\u2019ORDRE des cl\xE9s entre dans l\u2019empreinte d\u2019idempotence."
  )
};
var createRequestBodySchema = z9.object(createRequestBodyShape).describe("Corps de `POST /v1/requests`.");
var outcomeCodeSchema = z9.enum([
  "rejected_invalid_input",
  "rejected_use_case_unavailable",
  "rejected_user_unknown",
  "rejected_user_inactive",
  "rejected_cap_reached",
  "provider_unavailable",
  "provider_rejected",
  "provider_unconfirmed",
  "processing_error",
  "content_refused",
  "output_truncated",
  "invalid_output",
  "succeeded",
  "cancelled",
  "expired",
  "internal_error"
]).describe(
  "Issue finale d\u2019une requ\xEAte (16 valeurs), qui d\xE9termine la facturation. Sur une ressource, seules apparaissent : rejected_invalid_input, rejected_cap_reached, provider_unavailable, provider_rejected, provider_unconfirmed, processing_error, content_refused, output_truncated, invalid_output, succeeded, cancelled, expired, internal_error. Les autres (rejected_use_case_unavailable, rejected_user_unknown, rejected_user_inactive) ne sont jamais enregistr\xE9es : elles n\u2019existent que comme erreurs HTTP."
);
var requestStatusSchema = z9.enum([
  "paused",
  "queued",
  "running",
  "batched",
  "rejected",
  "succeeded",
  "failed",
  "cancelled",
  "expired"
]).describe(
  "Statut d\u2019une requ\xEAte. Terminaux (d\xE9finitifs, hors la fen\xEAtre r\xE9siduelle d\xE9crite dans le guide du document) : rejected, succeeded, failed, cancelled, expired. Non terminaux : paused, queued, running, batched. Particularit\xE9s : `queued` = en attente de prise en charge (ou de collecte pour un lot), annulable ; `running` = prise en charge par l\u2019ex\xE9cution, appel au fournisseur en cours, NON annulable (409 `not_cancellable`), sans webhook ; `paused` n\u2019est pas un \xE9chec."
);
var requestOutcomeShape = {
  code: outcomeCodeSchema,
  billable: z9.boolean().describe(
    "Vrai si cette issue est factur\xE9e. Le prix n\u2019est expos\xE9 que si l\u2019utilisateur a le droit de voir les montants (`price`)."
  ),
  message: z9.string().nullable().describe(
    "Motif lisible, en fran\xE7ais, de certains rejets enregistr\xE9s (ex. destination SMS non autoris\xE9e, SMS vide ou trop long), identique au `detail` de la r\xE9ponse 422, et d\u2019un SMS non envoy\xE9 (`provider_rejected`) car sa destination a \xE9t\xE9 retir\xE9e du cas d\u2019usage avant l\u2019envoi ; `null` dans tous les autres cas. Texte libre susceptible de changer : brancher la logique sur `code`, jamais sur `message`."
  )
};
var REQUEST_OUTCOME_DESCRIPTION = "Issue de la requ\xEAte. `null` tant que la requ\xEAte n\u2019est pas dans un statut terminal ; renseign\xE9e dans tous les statuts terminaux.";
var requestOutcomeSchema = z9.object(requestOutcomeShape).nullable().describe(REQUEST_OUTCOME_DESCRIPTION);
var textResultSchema = z9.object({
  kind: z9.literal("text").describe("Discriminant : r\xE9sultat texte."),
  text: z9.string().describe("Texte produit par le mod\xE8le (concat\xE9nation de toutes ses parties texte). Peut \xEAtre vide."),
  json: z9.custom((v) => v !== void 0).describe(
    "R\xE9sultat de `JSON.parse(text)` appliqu\xE9 au texte COMPLET, ou `null` si le texte n\u2019est pas un JSON valide (y compris entour\xE9 de balises ```). Rempli m\xEAme sans `responseFormat` (un texte \xAB 42 \xBB donne `json: 42`). Avec `responseFormat.json_schema`, `json` est l\u2019objet valid\xE9 contre votre sch\xE9ma ; si la validation \xE9choue la requ\xEAte est en `failed` / `invalid_output`. Traiter `null` comme un \xE9chec de format."
  )
}).describe("R\xE9sultat d\u2019une requ\xEAte de kind `text`.");
var imageResultSchema = z9.object({
  kind: z9.literal("image").describe("Discriminant : r\xE9sultat image."),
  fileIds: z9.array(fileIdSchema).describe(
    "Identifiants `fil_\u2026` des images g\xE9n\xE9r\xE9es (une par image). \xC0 t\xE9l\xE9charger via `GET /v1/files/{id}/content` avec les m\xEAmes en-t\xEAtes d\u2019authentification ; conserv\xE9es 7 jours."
  )
}).describe("R\xE9sultat d\u2019une requ\xEAte de kind `image`.");
var smsResultSchema = z9.object({
  kind: z9.literal("sms").describe("Discriminant : r\xE9sultat d\u2019un envoi de SMS."),
  messageId: z9.string().min(1).nullable().describe(
    "Identifiant du message chez l\u2019op\xE9rateur SMS, \xE0 communiquer au support ; `null` dans le cas rare o\xF9 l\u2019op\xE9rateur a accept\xE9 le message sans renvoyer d\u2019identifiant."
  ),
  segments: z9.number().int().positive().describe("Nombre de SMS (segments) envoy\xE9s, et factur\xE9s si la r\xE8gle de prix est au SMS."),
  encoding: z9.enum(["gsm7", "ucs2"]).describe(
    "Encodage retenu : `gsm7` (alphabet SMS standard : 160 caract\xE8res pour un SMS seul, puis 153 par SMS) ou `ucs2` (Unicode : 70, puis 67)."
  ),
  sender: z9.string().describe("Exp\xE9diteur (nom affich\xE9) effectivement utilis\xE9 : celui du cas d\u2019usage au moment de l\u2019envoi.")
}).describe(
  "R\xE9sultat d\u2019une requ\xEAte de kind `sms`. `succeeded` signifie que l\u2019op\xE9rateur SMS a ACCEPT\xC9 le message pour acheminement, pas qu\u2019il a \xE9t\xE9 re\xE7u : aucun accus\xE9 de r\xE9ception n\u2019est suivi."
);
var requestResultSchema = z9.discriminatedUnion("kind", [textResultSchema, imageResultSchema, smsResultSchema]).describe(
  "R\xE9sultat, discrimin\xE9 par `kind` (`text`, `image`, `sms`). Si le mod\xE8le ne renvoie aucune image pour une requ\xEAte image, le r\xE9sultat est de kind `text` (texte \xE9ventuel, `json` null) et la requ\xEAte est tout de m\xEAme `succeeded`."
);
var requestResourceSchema = z9.object({
  id: requestIdSchema,
  status: requestStatusSchema,
  useCase: z9.string().describe("Cl\xE9 du cas d\u2019usage de la requ\xEAte (celle envoy\xE9e dans `useCase`)."),
  reference: z9.string().nullable().describe("Votre r\xE9f\xE9rence m\xE9tier, ou `null` si aucune n\u2019a \xE9t\xE9 fournie."),
  metadata: z9.record(z9.string()).describe("Vos m\xE9tadonn\xE9es telles qu\u2019envoy\xE9es (`{}` si absentes)."),
  outcome: requestOutcomeSchema,
  result: requestResultSchema.nullable().describe(
    "R\xE9sultat. `null` si la requ\xEAte n\u2019est pas termin\xE9e, si elle s\u2019est termin\xE9e sans contenu (rejected, cancelled, expired, provider_unavailable, provider_rejected, provider_unconfirmed, internal_error), ou si le contenu a \xE9t\xE9 purg\xE9 (30 jours apr\xE8s la fin). Aucun champ ne signale la purge : un `result` null sur une requ\xEAte termin\xE9e depuis plus de 30 jours avec `outcome.code` succeeded signifie \xAB purg\xE9 \xBB. Pr\xE9sent aussi, partiellement, sur certaines issues d\u2019\xE9chec factur\xE9es des requ\xEAtes IA (content_refused, output_truncated, invalid_output, parfois processing_error). Un SMS n\u2019a de r\xE9sultat que sur `succeeded`."
  ),
  price: moneySchema.nullable().optional().describe(
    "Prix factur\xE9 de la requ\xEAte. La cl\xE9 est ABSENTE (pas `null`) quand l\u2019utilisateur identifi\xE9 par `X-Majuscule-User` DE L\u2019APPEL COURANT n\u2019a pas le droit de voir les montants. Quand le droit existe : `null` tant que la requ\xEAte n\u2019est pas termin\xE9e ou si l\u2019issue n\u2019est pas facturable, sinon le montant brut fig\xE9 \xE0 la finalisation (jamais modifi\xE9 par les remises ou ajustements de facturation)."
  ),
  createdAt: timestampSchema("Date de cr\xE9ation de la requ\xEAte."),
  finishedAt: timestampSchema("Date de fin (passage dans un statut terminal).").nullable().describe(
    "Date de fin, ISO 8601 UTC avec millisecondes. `null` tant que la requ\xEAte n\u2019est pas terminale. Pour une requ\xEAte `expired`, c\u2019est l\u2019instant du balayage d\u2019expiration (horaire), pas cr\xE9ation + 30 jours."
  )
}).describe(
  "Ressource Request : \xE9tat d\u2019une requ\xEAte (IA ou SMS). Le m\xEAme objet est renvoy\xE9 \xE0 la cr\xE9ation (202/200), \xE0 la lecture, dans la liste et \xE0 l\u2019annulation."
);
var requestListSchema = z9.object({
  items: z9.array(requestResourceSchema).describe(
    "Requ\xEAtes du CLIENT (tous utilisateurs confondus), de la plus r\xE9cente \xE0 la plus ancienne, 50 au maximum."
  ),
  nextCursor: z9.string().nullable().describe(
    "Curseur (valeur opaque) de la page suivante, ou `null` s\u2019il n\u2019y a pas de page suivante : il n\u2019est non nul que si au moins un \xE9l\xE9ment suit, donc une derni\xE8re page pleine (50) renvoie `null`. Le renvoyer tel quel dans le param\xE8tre `cursor` de `GET /v1/requests`, avec les m\xEAmes filtres, jusqu\u2019\xE0 obtenir `null`. Un curseur inconnu ou appartenant \xE0 un autre client donne une page vide (`items: []`, `nextCursor: null`)."
  )
}).describe("Page de requ\xEAtes.");
var attemptUsageSchema = z9.object({
  inputTokens: z9.number(),
  outputTokens: z9.number(),
  cacheWriteTokens: z9.number(),
  cacheReadTokens: z9.number(),
  outputImages: z9.number(),
  /** SMS segments sent (lot 17); 0 for AI calls. */
  messages: z9.number()
});
var attemptDetailResourceSchema = z9.object({
  number: z9.number(),
  status: z9.enum(["sent", "completed", "abandoned"]),
  mode: z9.enum(["standard", "batch"]),
  outcome: outcomeCodeSchema.nullable(),
  /** `currency`: the entry's tariff currency; EUR means no conversion, so no USD cost nor rate. */
  model: z9.object({
    id: z9.string(),
    provider: z9.string(),
    providerModelId: z9.string(),
    currency: tariffCurrencySchema
  }).nullable(),
  usage: attemptUsageSchema.nullable(),
  tariffId: z9.string().nullable(),
  exchangeRate: z9.string().nullable(),
  /**
   * Provider cost in euros / US dollars (decimal strings); null when unknown.
   * `costUsd` and `exchangeRate` are null for a tariff in EUR (never converted).
   */
  costEur: z9.string().nullable(),
  costUsd: z9.string().nullable(),
  costUnknown: z9.boolean(),
  providerRequestId: z9.string().nullable(),
  errorDetail: z9.string().nullable(),
  /** Exact body sent to the provider and the raw response; null if purged/uncaptured (PRD 21). */
  providerRequest: z9.unknown().nullable(),
  providerResponse: z9.unknown().nullable(),
  startedAt: z9.string(),
  finishedAt: z9.string().nullable()
});
var requestFileResourceSchema = z9.object({
  id: z9.string(),
  mimeType: z9.string(),
  sizeBytes: z9.number().int(),
  pages: z9.number().int().nullable()
});
var requestDetailResourceSchema = z9.object({
  id: z9.string(),
  status: requestStatusSchema,
  reference: z9.string().nullable(),
  client: z9.object({ id: z9.string(), name: z9.string() }).nullable(),
  /** Environment of the submitting key; a staging request is never billed (D-ENV.1). */
  environment: environmentSchema,
  useCase: z9.string().nullable(),
  /** Kind of the use case, to render the input and the result; null when the use case is unresolvable (lot 17). */
  useCaseKind: useCaseKindSchema.nullable(),
  /** Billed units counted at submission: PDF pages + attached images, 1 per image generation, SMS segments. */
  units: z9.number().int(),
  outcome: requestOutcomeSchema,
  billable: z9.boolean(),
  amount: z9.string(),
  providerCost: z9.string(),
  metadata: z9.record(z9.string()),
  contentPurged: z9.boolean(),
  /** Normalized incoming request (`RequestInput`) and result (`RequestResult`); null if purged. */
  input: z9.unknown().nullable(),
  result: z9.unknown().nullable(),
  createdAt: z9.string(),
  startedAt: z9.string().nullable(),
  finishedAt: z9.string().nullable(),
  attempts: z9.array(attemptDetailResourceSchema),
  /** Documents sent with the request and documents it produced; empty once purged. */
  files: z9.object({
    inputs: z9.array(requestFileResourceSchema),
    outputs: z9.array(requestFileResourceSchema)
  })
});
var usageMonthSchema = z9.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).describe("Mois de facturation courant, format YYYY-MM, fuseau Europe/Paris (bascule \xE0 minuit heure de Paris).");
var percentUsedSchema = z9.number().nullable().describe(
  "Pourcentage du plafond consomm\xE9 = consommation de l\u2019UTILISATEUR / plafond applicable \xD7 100, arrondi \xE0 une d\xE9cimale, NON plafonn\xE9 \xE0 100 (peut d\xE9passer 100). Le plafond applicable est le plafond de l\u2019utilisateur, \xE0 d\xE9faut celui du client. `null` si aucun plafond ne s\u2019applique (ou plafond \xE9gal \xE0 0). M\xEAme quand seul le plafond du client s\u2019applique, la consommation prise en compte est celle de l\u2019utilisateur."
);
var usageMeWithAmountsSchema = z9.object({
  month: usageMonthSchema,
  spent: moneySchema.describe(
    "Consommation de l\u2019utilisateur sur le mois : montants factur\xE9s bruts + r\xE9servations des requ\xEAtes en cours."
  ),
  cap: moneySchema.nullable().describe("Plafond applicable (plafond de l\u2019utilisateur, \xE0 d\xE9faut plafond du client), ou `null` si aucun."),
  percentUsed: percentUsedSchema
}).describe("Consommation avec montants (utilisateur autoris\xE9 \xE0 voir les montants).");
var usageMeWithoutAmountsSchema = z9.object({
  month: usageMonthSchema,
  percentUsed: percentUsedSchema
}).describe("Consommation sans montants (utilisateur non autoris\xE9 \xE0 voir les montants) : uniquement le pourcentage.");
var usageMeSchema = z9.union([usageMeWithAmountsSchema, usageMeWithoutAmountsSchema]).describe(
  "Consommation de l\u2019utilisateur courant. Deux formes selon son droit `canViewAmounts` : avec montants (`spent`, `cap`) ou sans (`percentUsed` seul)."
);
var setMyCapSchema = z9.object({
  cap: z9.string().regex(/^\d+(\.\d{1,6})?$/).nullable().describe(
    "Nouveau plafond mensuel de l\u2019utilisateur, en euros (cha\xEEne d\xE9cimale, 0 \xE0 6 d\xE9cimales, ex. \xAB 25 \xBB ou \xAB 12.5 \xBB), ou `null` pour supprimer son plafond personnel. La cl\xE9 est OBLIGATOIRE. 0 est accept\xE9 (bloque toute nouvelle requ\xEAte). Un nombre JSON, \xAB .5 \xBB ou plus de 6 d\xE9cimales sont refus\xE9s (422). Doit \xEAtre inf\xE9rieur ou \xE9gal au plafond du client."
  )
}).describe("Corps de `PATCH /v1/usage/me/cap`.");
var setMyCapResponseSchema = z9.object({
  cap: z9.string().nullable().describe(
    "Plafond personnel enregistr\xE9 : cha\xEEne d\xE9cimale en euros SANS devise (forme diff\xE9rente de `Money`), ou `null` si l\u2019utilisateur n\u2019a plus de plafond personnel."
  )
}).describe("Plafond personnel de l\u2019utilisateur apr\xE8s modification.");

// src/files.ts
import { z as z10 } from "zod";
var acceptedFileMimeTypeSchema = z10.enum(["application/pdf", "image/png", "image/jpeg", "image/webp"]).describe(
  "Type MIME d\xE9tect\xE9 par la passerelle \xE0 partir des premiers octets du fichier (jamais d\u2019apr\xE8s l\u2019extension ni le `Content-Type` envoy\xE9)."
);
var fileResourceSchema = z10.object({
  id: fileIdSchema,
  mimeType: acceptedFileMimeTypeSchema,
  sizeBytes: z10.number().int().positive().describe("Taille du fichier en octets (20 971 520 au maximum)."),
  pages: z10.number().int().positive().describe(
    "Nombre de pages : celui du PDF, ou 1 pour une image. Sert d\u2019unit\xE9 de facturation pour les cas d\u2019usage factur\xE9s \xE0 la page."
  ),
  expiresAt: timestampSchema("Date d\u2019expiration explicite du fichier.").nullable().describe(
    "Toujours `null` \xE0 ce jour : un fichier n\u2019a pas de date d\u2019expiration explicite. Il est cependant supprim\xE9 environ 7 jours apr\xE8s son d\xE9p\xF4t (jamais avant ; suppression quotidienne), sauf tant qu\u2019une requ\xEAte non terminale le r\xE9f\xE9rence : le red\xE9poser pour toute requ\xEAte ult\xE9rieure."
  )
}).describe("Fichier d\xE9pos\xE9, utilisable ensuite par son `id` dans une requ\xEAte.");

// src/billing.ts
import { z as z11 } from "zod";
var createAdjustmentSchema = z11.object({
  clientId: z11.string().min(1),
  percent: z11.number().int().min(1).max(100),
  reason: z11.string().min(1).max(500),
  requestIds: z11.array(z11.string().min(1)).min(1)
});
var monthSchema3 = z11.string().regex(/^\d{4}-\d{2}$/, "Format attendu : YYYY-MM.");
var closeBillingPeriodSchema = z11.object({ month: monthSchema3 });
var closeMonthSchema = z11.object({ month: monthSchema3 });
var setBillingDeferralSchema = z11.object({ deferred: z11.boolean() });
var stripeWebhookEventSchema = z11.discriminatedUnion("kind", [
  z11.object({ kind: z11.literal("checkout.session.completed"), customerId: z11.string().min(1) }),
  z11.object({
    kind: z11.literal("mandate.updated"),
    customerId: z11.string().min(1),
    active: z11.boolean()
  }),
  z11.object({ kind: z11.literal("payment_intent.succeeded"), paymentIntentId: z11.string().min(1) }),
  z11.object({
    kind: z11.literal("payment_intent.payment_failed"),
    paymentIntentId: z11.string().min(1)
  }),
  z11.object({
    kind: z11.literal("charge.dispute.created"),
    customerId: z11.string().min(1).nullable()
  })
]);
var parsedStripeEventSchema = z11.object({
  eventId: z11.string().min(1),
  event: stripeWebhookEventSchema
});

// src/webhooks.ts
import { z as z12 } from "zod";
var webhookDeliveryResourceSchema = z12.object({
  id: z12.string(),
  clientId: z12.string(),
  clientName: z12.string().nullable(),
  eventType: z12.string(),
  status: z12.enum(["pending", "delivered", "failed"]),
  attempts: z12.number(),
  requestId: z12.string().nullable(),
  lastResponseStatus: z12.number().nullable(),
  lastError: z12.string().nullable(),
  nextAttemptAt: z12.string().nullable(),
  createdAt: z12.string()
});

// src/webhook-events.ts
import { z as z13 } from "zod";
var WEBHOOK_EVENT_TYPES = [
  "request.succeeded",
  "request.failed",
  "request.paused",
  "request.resumed",
  "request.cancelled",
  "request.expired"
];
var webhookEventTypeSchema = z13.enum(WEBHOOK_EVENT_TYPES).describe(
  "Type d\u2019\xE9v\xE9nement (6 valeurs). Aucun \xE9v\xE9nement n\u2019existe pour `rejected`, `running`, `batched` ni pour la cr\xE9ation en `queued`."
);
var referenceSchema = z13.string().nullable().describe("Votre `reference` de la requ\xEAte, ou `null` si aucune n\u2019a \xE9t\xE9 fournie.");
var dataBase = {
  requestId: requestIdSchema.describe(
    "Identifiant `req_\u2026` de la requ\xEAte concern\xE9e. \xC0 relire via `GET /v1/requests/{requestId}` pour obtenir l\u2019\xE9tat et le r\xE9sultat."
  ),
  reference: referenceSchema
};
function envelope(type, data) {
  return z13.object({
    id: webhookDeliveryIdSchema.describe(
      "Identifiant `whd_\u2026` de la livraison, identique \xE0 l\u2019en-t\xEAte `X-Majuscule-Delivery` et identique \xE0 chaque nouvelle tentative de la m\xEAme livraison : s\u2019en servir pour d\xE9doublonner."
    ),
    type: z13.literal(type).describe("Type d\u2019\xE9v\xE9nement (discriminant)."),
    createdAt: timestampSchema("Date de cr\xE9ation de l\u2019\xE9v\xE9nement (pas l\u2019heure d\u2019envoi : inchang\xE9e \xE0 chaque tentative de la m\xEAme livraison)."),
    data
  });
}
var webhookRequestSucceededSchema = envelope(
  "request.succeeded",
  z13.object({
    ...dataBase,
    status: z13.literal("succeeded").describe("Statut de la requ\xEAte \xE0 cet instant."),
    useCase: useCaseIdSchema.describe(
      "ATTENTION : c\u2019est l\u2019IDENTIFIANT interne `uc_\u2026` du cas d\u2019usage, PAS sa cl\xE9 (ex. \xAB facture.extraction \xBB). Pour la cl\xE9, relire la requ\xEAte (`useCase`)."
    )
  })
).describe("La requ\xEAte est termin\xE9e avec succ\xE8s. Le r\xE9sultat se lit via `GET /v1/requests/{requestId}`.");
var webhookRequestFailedSchema = envelope(
  "request.failed",
  z13.object({
    ...dataBase,
    status: z13.literal("failed").describe("Statut de la requ\xEAte \xE0 cet instant."),
    useCase: useCaseIdSchema.describe(
      "ATTENTION : c\u2019est l\u2019IDENTIFIANT interne `uc_\u2026` du cas d\u2019usage, PAS sa cl\xE9. Pour la cl\xE9, relire la requ\xEAte (`useCase`)."
    )
  })
).describe(
  "La requ\xEAte est termin\xE9e en \xE9chec (toute issue d\u2019\xE9chec, factur\xE9e ou non). L\u2019issue exacte (`outcome.code`) se lit via `GET /v1/requests/{requestId}` ; aucun motif n\u2019est port\xE9 par le webhook."
);
var webhookRequestPausedSchema = envelope(
  "request.paused",
  z13.object({
    ...dataBase,
    status: z13.literal("paused").describe("Statut de la requ\xEAte \xE0 cet instant.")
  })
).describe(
  "La requ\xEAte est en pause (client en pause manuelle, plafond de s\xE9curit\xE9 ou plafond plateforme atteint). Ce n\u2019est pas un \xE9chec : attendre `request.resumed` ou `request.expired`, ou annuler. Aucun motif n\u2019est communiqu\xE9."
);
var webhookRequestResumedSchema = envelope(
  "request.resumed",
  z13.object({
    ...dataBase,
    status: z13.literal("queued").describe("Statut de la requ\xEAte \xE0 cet instant : repass\xE9e en `queued`.")
  })
).describe("La requ\xEAte mise en pause a \xE9t\xE9 reprise et repasse en file (`queued`).");
var webhookRequestCancelledSchema = envelope(
  "request.cancelled",
  z13.object({
    ...dataBase,
    status: z13.literal("cancelled").describe("Statut de la requ\xEAte \xE0 cet instant.")
  })
).describe(
  "La requ\xEAte (`queued` ou `paused`) a \xE9t\xE9 annul\xE9e via `POST /v1/requests/{id}/cancel` : elle n\u2019est pas ex\xE9cut\xE9e et n\u2019est pas factur\xE9e, aucun \xE9v\xE9nement de fin ne suit (hors fen\xEAtre r\xE9siduelle du guide du document : relire la requ\xEAte en cas de doute)."
);
var webhookRequestExpiredSchema = envelope(
  "request.expired",
  z13.object({
    ...dataBase,
    status: z13.literal("expired").describe("Statut de la requ\xEAte \xE0 cet instant.")
  })
).describe("La requ\xEAte est rest\xE9e en pause trop longtemps (30 jours apr\xE8s sa cr\xE9ation par d\xE9faut) et a expir\xE9.");
var webhookEventSchema = z13.discriminatedUnion("type", [
  webhookRequestSucceededSchema,
  webhookRequestFailedSchema,
  webhookRequestPausedSchema,
  webhookRequestResumedSchema,
  webhookRequestCancelledSchema,
  webhookRequestExpiredSchema
]).describe("Enveloppe d\u2019un webhook sortant, discrimin\xE9e par `type`.");

// src/crm.ts
import { z as z14 } from "zod";
var text = (max) => z14.string().trim().min(1).max(max);
var optionalText = (max) => z14.string().trim().max(max).nullable().optional();
var dateSchema = z14.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format attendu : YYYY-MM-DD.");
var timeSchema = z14.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Format attendu : HH:MM.");
var monthSchema4 = z14.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Format attendu : YYYY-MM.");
var companyOriginSchema = z14.enum([
  "network",
  "word_of_mouth",
  "existing_client",
  "linkedin",
  "website",
  "other"
]);
var companyStatusSchema = z14.enum(["prospect", "client", "former_client"]);
var contactRoleSchema = z14.enum(["decider", "user", "billing", "tech"]);
var dealModelSchema = z14.enum(["custom", "shared_license"]);
var openStageSchema = z14.enum([
  "to_qualify",
  "r1_planned",
  "r1_done",
  "proposal_presented",
  "specifications",
  "signature_pending"
]);
var dealStageSchema = z14.enum([...openStageSchema.options, "won", "lost"]);
var lostReasonSchema = z14.enum([
  "price",
  "timing",
  "competitor",
  "no_need",
  "no_response",
  "other"
]);
var reminderTypeSchema = z14.enum(["call", "email", "meeting", "task"]);
var reminderTimingSchema = z14.enum(["late", "today", "upcoming"]);
var loggedActivityKindSchema = z14.enum(["note", "call", "meeting", "email"]);
var contactFieldsShape = {
  firstName: text(100),
  lastName: text(100),
  jobTitle: optionalText(200),
  email: z14.string().trim().email().max(200).nullable().optional(),
  phone: optionalText(40)
};
var createCompanySchema = z14.object({
  name: text(200),
  legalName: optionalText(200),
  /** 9 digits; spaces and a SIRET are accepted and normalized. */
  siren: optionalText(20),
  nafCode: optionalText(20),
  headcount: optionalText(100),
  address: optionalText(300),
  sector: optionalText(100),
  origin: companyOriginSchema,
  primaryContact: z14.object(contactFieldsShape).nullable().optional(),
  /** Also open a deal "À qualifier" with a call reminder tomorrow at 9:00. */
  createQualifyingDeal: z14.boolean().default(false)
});
var updateCompanySchema = z14.object({
  name: text(200),
  legalName: optionalText(200),
  siren: optionalText(20),
  nafCode: optionalText(20),
  headcount: optionalText(100),
  address: optionalText(300),
  sector: optionalText(100),
  origin: companyOriginSchema,
  pinnedNote: optionalText(2e3),
  primaryContactId: z14.string().nullable()
}).partial();
var createContactSchema = z14.object({
  ...contactFieldsShape,
  roles: z14.array(contactRoleSchema).default([]),
  /** Make this contact the company's primary contact. */
  makePrimary: z14.boolean().default(false)
});
var updateContactSchema = z14.object({ ...contactFieldsShape, roles: z14.array(contactRoleSchema) }).partial();
var reminderFieldsShape = {
  subject: text(200),
  type: reminderTypeSchema,
  /** Paris calendar date. */
  dueDate: dateSchema,
  /** Paris wall-clock time. */
  dueTime: timeSchema,
  contactId: z14.string().nullable().optional(),
  note: optionalText(2e3)
};
var createDealSchema = z14.object({
  companyId: z14.string(),
  title: text(200),
  model: dealModelSchema,
  stage: openStageSchema.default("to_qualify"),
  /** Percent; defaults to the stage's probability. */
  probability: z14.number().int().min(0).max(100).optional(),
  setup: euroAmountSchema.nullable().optional(),
  monthly: euroAmountSchema.nullable().optional(),
  /** Monthly AI fees estimate (lot 21). */
  aiMonthly: euroAmountSchema.nullable().optional(),
  expectedCloseDate: dateSchema.nullable().optional(),
  contactIds: z14.array(z14.string()).default([]),
  /** Team member carrying the deal (admin only); defaults to the creator (lot 20). */
  ownerId: z14.string().nullable().optional(),
  /** An open deal always has a next action. */
  nextAction: z14.object(reminderFieldsShape)
});
var updateDealSchema = z14.object({
  title: text(200),
  probability: z14.number().int().min(0).max(100),
  setup: euroAmountSchema.nullable(),
  monthly: euroAmountSchema.nullable(),
  aiMonthly: euroAmountSchema.nullable(),
  expectedCloseDate: dateSchema.nullable(),
  contactIds: z14.array(z14.string()),
  /** Reassign the deal to a team member (admin only, lot 20). */
  ownerId: z14.string()
}).partial();
var moveDealStageSchema = z14.object({ stage: openStageSchema });
var markDealLostSchema = z14.object({
  reason: lostReasonSchema,
  competitor: optionalText(200),
  comment: optionalText(2e3),
  /** Schedule a renewed contact on the company that many months later, or none. */
  followUpInMonths: z14.union([z14.literal(3), z14.literal(6), z14.literal(12)]).nullable().default(null)
});
var newAccountSchema = z14.object({
  name: text(200),
  legalName: text(200),
  billingEmail: z14.string().trim().email(),
  /** Used only when Qonto has no client with the company's SIREN yet. */
  billingAddress: billingAddressSchema.nullable().optional(),
  safetyCap: euroAmountSchema,
  clientCap: euroAmountSchema.nullable().optional(),
  defaultUserCap: euroAmountSchema.nullable().optional()
});
var convertDealSchema = z14.object({
  /** Signature date. */
  signedOn: dateSchema,
  /** First subscription month: the subscription becomes a monthly recurring charge from it. */
  subscriptionStartMonth: monthSchema4,
  /**
   * `new`: a prospect becomes a customer, its billed account is created (its
   * staging ERP uses staging keys of that account).
   * `existing`: a company already customer wins another brick; the subscription
   * is added to one of its accounts.
   */
  target: z14.discriminatedUnion("kind", [
    z14.object({
      kind: z14.literal("new"),
      account: newAccountSchema
    }),
    z14.object({ kind: z14.literal("existing"), clientId: z14.string() })
  ]),
  /** Issue the SEPA mandate link of the (main) account, to copy and send. */
  issueMandateLink: z14.boolean().default(true)
});
var discoveryReportSchema = z14.object({
  context: z14.string().trim().max(4e3).default(""),
  pains: z14.string().trim().max(4e3).default(""),
  currentTools: z14.string().trim().max(4e3).default(""),
  decisionMaker: z14.string().trim().max(4e3).default(""),
  budget: z14.string().trim().max(4e3).default(""),
  timeline: z14.string().trim().max(4e3).default("")
});
var logActivitySchema = z14.discriminatedUnion("kind", [
  z14.object({
    kind: loggedActivityKindSchema,
    companyId: z14.string(),
    dealId: z14.string().nullable().optional(),
    body: text(1e4),
    pinned: z14.boolean().default(false)
  }),
  z14.object({
    kind: z14.literal("discovery"),
    companyId: z14.string(),
    dealId: z14.string().nullable().optional(),
    report: discoveryReportSchema,
    pinned: z14.boolean().default(true)
  })
]);
var updateActivitySchema = z14.object({ pinned: z14.boolean(), report: discoveryReportSchema }).partial();
var createReminderSchema = z14.object({
  companyId: z14.string(),
  dealId: z14.string().nullable().optional(),
  ...reminderFieldsShape
});
var updateReminderSchema = z14.object(reminderFieldsShape).partial();
var dealDocumentCategorySchema = z14.enum([
  "proposal",
  "specifications",
  "contract",
  "other"
]);
var createDealDocumentSchema = z14.object({
  label: text(200),
  url: z14.string().trim().url().max(2e3),
  category: dealDocumentCategorySchema.default("other")
});
var uploadDealDocumentFieldsSchema = z14.object({
  label: z14.string().trim().max(200).optional(),
  category: dealDocumentCategorySchema.default("other")
});
var linkCompanyAccountSchema = z14.object({ clientId: z14.string() });
var nextActionResourceSchema = z14.object({
  reminderId: z14.string(),
  subject: z14.string(),
  type: reminderTypeSchema,
  dueAt: z14.string(),
  timing: reminderTimingSchema
});
var followUpFlagSchema = z14.enum(["late", "no_next_action"]);
var dealSummaryResourceSchema = z14.object({
  id: z14.string(),
  companyId: z14.string(),
  companyName: z14.string(),
  title: z14.string(),
  model: dealModelSchema,
  stage: dealStageSchema,
  probability: z14.number(),
  setup: z14.string().nullable(),
  monthly: z14.string().nullable(),
  /** Monthly AI fees estimate (lot 21); not in the weighted amount. */
  aiMonthly: z14.string().nullable(),
  /** Set-up + 12 × subscription; null while not priced. */
  twelveMonthValue: z14.string().nullable(),
  /** Weighted amount of an open deal; null when closed or not priced. */
  weighted: z14.string().nullable(),
  expectedCloseDate: z14.string().nullable(),
  stageEnteredAt: z14.string(),
  /** Paris calendar days in the current stage. */
  daysInStage: z14.number(),
  nextAction: nextActionResourceSchema.nullable(),
  flag: followUpFlagSchema.nullable(),
  /** Signature (won) or loss date. */
  closedOn: z14.string().nullable(),
  lostReason: lostReasonSchema.nullable(),
  /** Team member carrying the deal (lot 20); null: no owner. */
  owner: z14.object({ id: z14.string(), name: z14.string() }).nullable(),
  createdAt: z14.string()
});
var pipelineStatsResourceSchema = z14.object({
  weighted: z14.string(),
  openCount: z14.number(),
  /** Monthly subscriptions of the deals won this calendar quarter (Paris). */
  signedMonthlyThisQuarter: z14.string(),
  wonThisQuarter: z14.number(),
  quarterStart: z14.string(),
  signatureRateAfterR1: z14.number().nullable(),
  closedAfterR1: z14.number(),
  wonAfterR1: z14.number(),
  averageCycleDays: z14.number().nullable(),
  noNextAction: z14.number(),
  lateReminders: z14.number()
});
var pipelineResourceSchema = z14.object({
  deals: z14.array(dealSummaryResourceSchema),
  stats: pipelineStatsResourceSchema
});
var contactResourceSchema = z14.object({
  id: z14.string(),
  companyId: z14.string(),
  firstName: z14.string(),
  lastName: z14.string(),
  jobTitle: z14.string().nullable(),
  email: z14.string().nullable(),
  phone: z14.string().nullable(),
  roles: z14.array(contactRoleSchema),
  primary: z14.boolean()
});
var companyListItemResourceSchema = z14.object({
  id: z14.string(),
  name: z14.string(),
  siren: z14.string().nullable(),
  sector: z14.string().nullable(),
  address: z14.string().nullable(),
  status: companyStatusSchema,
  primaryContact: z14.object({ id: z14.string(), name: z14.string(), jobTitle: z14.string().nullable() }).nullable(),
  openDeals: z14.number(),
  /** Weighted amount of the open deals; null when none is priced. */
  weighted: z14.string().nullable(),
  /** Monthly subscriptions of the won deals; null when none. */
  signedMonthly: z14.string().nullable(),
  lastActivityAt: z14.string().nullable(),
  nextAction: nextActionResourceSchema.nullable(),
  flag: followUpFlagSchema.nullable()
});
var companiesResourceSchema = z14.object({
  companies: z14.array(companyListItemResourceSchema),
  stats: z14.object({
    prospects: z14.number(),
    prospectsWithOpenDeal: z14.number(),
    clients: z14.number(),
    linkedAccounts: z14.number(),
    /** Monthly subscriptions of the won deals of current clients. */
    signedMonthly: z14.string()
  })
});
var systemEventResourceSchema = z14.discriminatedUnion("type", [
  z14.object({ type: z14.literal("company_created") }),
  z14.object({ type: z14.literal("deal_created"), dealTitle: z14.string() }),
  z14.object({
    type: z14.literal("stage_changed"),
    dealTitle: z14.string(),
    from: dealStageSchema,
    to: dealStageSchema
  }),
  z14.object({ type: z14.literal("deal_won"), dealTitle: z14.string() }),
  z14.object({ type: z14.literal("deal_lost"), dealTitle: z14.string(), reason: lostReasonSchema }),
  z14.object({ type: z14.literal("accounts_created"), accountNames: z14.array(z14.string()) }),
  z14.object({
    type: z14.literal("reminder_created"),
    subject: z14.string(),
    dueAt: z14.string(),
    automatic: z14.boolean()
  }),
  z14.object({ type: z14.literal("reminder_done"), subject: z14.string() }),
  z14.object({ type: z14.literal("proposal_sent"), dealTitle: z14.string(), version: z14.number() }),
  z14.object({ type: z14.literal("proposal_accepted"), dealTitle: z14.string(), version: z14.number() }),
  z14.object({ type: z14.literal("proposal_declined"), dealTitle: z14.string(), version: z14.number() }),
  z14.object({ type: z14.literal("installment_invoiced"), dealTitle: z14.string(), label: z14.string() }),
  z14.object({ type: z14.literal("installment_paid"), dealTitle: z14.string(), label: z14.string() }),
  z14.object({ type: z14.literal("document_added"), dealTitle: z14.string(), label: z14.string() }),
  z14.object({
    type: z14.literal("appointment_booked"),
    title: z14.string(),
    typeName: z14.string().nullable(),
    startAt: z14.string()
  }),
  z14.object({
    type: z14.literal("appointment_rescheduled"),
    title: z14.string(),
    typeName: z14.string().nullable(),
    startAt: z14.string()
  }),
  z14.object({
    type: z14.literal("appointment_cancelled"),
    title: z14.string(),
    typeName: z14.string().nullable()
  })
]);
var activityResourceSchema = z14.object({
  id: z14.string(),
  companyId: z14.string(),
  dealId: z14.string().nullable(),
  dealTitle: z14.string().nullable(),
  content: z14.discriminatedUnion("kind", [
    z14.object({ kind: loggedActivityKindSchema, body: z14.string() }),
    z14.object({ kind: z14.literal("discovery"), report: discoveryReportSchema }),
    z14.object({ kind: z14.literal("system"), event: systemEventResourceSchema })
  ]),
  pinned: z14.boolean(),
  occurredAt: z14.string(),
  author: z14.string()
});
var reminderResourceSchema = z14.object({
  id: z14.string(),
  companyId: z14.string(),
  companyName: z14.string(),
  dealId: z14.string().nullable(),
  dealTitle: z14.string().nullable(),
  contactId: z14.string().nullable(),
  contactName: z14.string().nullable(),
  subject: z14.string(),
  type: reminderTypeSchema,
  dueAt: z14.string(),
  /** Paris calendar date and time of `dueAt`. */
  dueDate: z14.string(),
  dueTime: z14.string(),
  note: z14.string().nullable(),
  automatic: z14.boolean(),
  doneAt: z14.string().nullable(),
  /** Null once done. */
  timing: reminderTimingSchema.nullable()
});
var companyAccountResourceSchema = z14.object({
  id: z14.string(),
  name: z14.string(),
  billingEmail: z14.string(),
  status: z14.enum(["active", "paused", "disabled"]),
  /** Consumption of the current month (finalized + reserved). */
  monthUsage: z14.string(),
  safetyCap: z14.string(),
  createdAt: z14.string()
});
var appointmentTypeResourceSchema = z14.object({
  id: z14.string(),
  name: z14.string(),
  bookingUrl: z14.string(),
  calEventSlug: z14.string().nullable(),
  stageOnBooking: openStageSchema.nullable(),
  position: z14.number(),
  active: z14.boolean()
});
var appointmentTypesResourceSchema = z14.object({
  types: z14.array(appointmentTypeResourceSchema),
  /** URL to paste in Cal.com → Settings → Developer → Webhooks. */
  webhookUrl: z14.string(),
  /** False while CALCOM_WEBHOOK_SECRET is not set on the API. */
  webhookConfigured: z14.boolean()
});
var appointmentTypeFieldsShape = {
  name: text(50),
  bookingUrl: z14.string().trim().url().max(500),
  calEventSlug: z14.string().trim().max(100).nullable().optional(),
  stageOnBooking: openStageSchema.nullable().optional(),
  active: z14.boolean().optional()
};
var createAppointmentTypeSchema = z14.object(appointmentTypeFieldsShape);
var updateAppointmentTypeSchema = z14.object(appointmentTypeFieldsShape).partial();
var appointmentResourceSchema = z14.object({
  id: z14.string(),
  companyId: z14.string().nullable(),
  companyName: z14.string().nullable(),
  dealId: z14.string().nullable(),
  dealTitle: z14.string().nullable(),
  typeId: z14.string().nullable(),
  typeName: z14.string().nullable(),
  title: z14.string(),
  startAt: z14.string(),
  endAt: z14.string(),
  status: z14.enum(["scheduled", "cancelled"]),
  attendeeName: z14.string().nullable(),
  attendeeEmail: z14.string().nullable(),
  meetingUrl: z14.string().nullable(),
  cancellationReason: z14.string().nullable()
});
var unmatchedAppointmentsResourceSchema = z14.object({
  appointments: z14.array(appointmentResourceSchema)
});
var attachAppointmentSchema = z14.object({
  companyId: z14.string(),
  dealId: z14.string().nullable().optional()
});
var companyDetailResourceSchema = z14.object({
  id: z14.string(),
  name: z14.string(),
  legalName: z14.string().nullable(),
  siren: z14.string().nullable(),
  nafCode: z14.string().nullable(),
  headcount: z14.string().nullable(),
  address: z14.string().nullable(),
  sector: z14.string().nullable(),
  origin: companyOriginSchema,
  pinnedNote: z14.string().nullable(),
  primaryContactId: z14.string().nullable(),
  status: companyStatusSchema,
  /** Creation of the first billed account; null for a prospect. */
  clientSince: z14.string().nullable(),
  createdAt: z14.string(),
  stats: z14.object({
    signedMonthly: z14.string().nullable(),
    openWeighted: z14.string().nullable(),
    openDeals: z14.number(),
    /** Consumption of the current month over all accounts; null for an employee (lot 20). */
    monthUsage: z14.string().nullable(),
    month: z14.string()
  }),
  contacts: z14.array(contactResourceSchema),
  deals: z14.array(dealSummaryResourceSchema),
  accounts: z14.array(companyAccountResourceSchema),
  activities: z14.array(activityResourceSchema),
  /** Open reminders, earliest first. */
  reminders: z14.array(reminderResourceSchema),
  /** Appointments, earliest first (lot 22). */
  appointments: z14.array(appointmentResourceSchema),
  appointmentTypes: z14.array(appointmentTypeResourceSchema)
});
var stageChangeResourceSchema = z14.object({
  from: dealStageSchema.nullable(),
  to: dealStageSchema,
  changedAt: z14.string()
});
var dealDocumentResourceSchema = z14.object({
  id: z14.string(),
  label: z14.string(),
  category: dealDocumentCategorySchema,
  kind: z14.enum(["link", "file"]),
  /** Link documents only; a stored PDF downloads from `/internal/crm/documents/:id/content`. */
  url: z14.string().nullable(),
  fileName: z14.string().nullable(),
  sizeBytes: z14.number().nullable(),
  createdAt: z14.string()
});
var plannedInstallmentSchema = z14.object({
  label: text(200),
  amount: euroAmountSchema,
  dueDate: dateSchema.nullable().optional()
});
var proposalFieldsShape = {
  title: z14.string().trim().max(200).nullable().optional(),
  setup: euroAmountSchema.nullable().optional(),
  monthly: euroAmountSchema.nullable().optional(),
  aiMonthly: euroAmountSchema.nullable().optional(),
  installments: z14.array(plannedInstallmentSchema).max(24).optional(),
  documentId: z14.string().nullable().optional(),
  note: z14.string().trim().max(2e3).nullable().optional()
};
var createProposalSchema = z14.object(proposalFieldsShape);
var updateProposalSchema = z14.object(proposalFieldsShape);
var sendProposalSchema = z14.object({ sentOn: dateSchema });
var decideProposalSchema = z14.object({ decidedOn: dateSchema });
var proposalStatusSchema = z14.enum(["draft", "sent", "accepted", "declined", "superseded"]);
var dealProposalResourceSchema = z14.object({
  id: z14.string(),
  version: z14.number(),
  title: z14.string(),
  status: proposalStatusSchema,
  sentOn: z14.string().nullable(),
  decidedOn: z14.string().nullable(),
  setup: z14.string().nullable(),
  monthly: z14.string().nullable(),
  aiMonthly: z14.string().nullable(),
  installments: z14.array(
    z14.object({ label: z14.string(), amount: z14.string(), dueDate: z14.string().nullable() })
  ),
  documentId: z14.string().nullable(),
  note: z14.string().nullable(),
  createdAt: z14.string()
});
var installmentStatusSchema = z14.enum(["planned", "invoiced", "paid"]);
var createInstallmentSchema = plannedInstallmentSchema;
var updateInstallmentSchema = z14.object({
  label: text(200),
  amount: euroAmountSchema,
  dueDate: dateSchema.nullable(),
  state: z14.discriminatedUnion("status", [
    z14.object({ status: z14.literal("planned") }),
    z14.object({
      status: z14.literal("invoiced"),
      invoicedOn: dateSchema,
      reference: z14.string().trim().max(100).nullable().optional()
    }),
    z14.object({
      status: z14.literal("paid"),
      invoicedOn: dateSchema.nullable().optional(),
      reference: z14.string().trim().max(100).nullable().optional(),
      paidOn: dateSchema
    })
  ])
}).partial();
var dealInstallmentResourceSchema = z14.object({
  id: z14.string(),
  position: z14.number(),
  label: z14.string(),
  amount: z14.string(),
  dueDate: z14.string().nullable(),
  status: installmentStatusSchema,
  invoicedOn: z14.string().nullable(),
  reference: z14.string().nullable(),
  paidOn: z14.string().nullable(),
  /** Due date passed, not paid. */
  late: z14.boolean()
});
var signatureSummaryResourceSchema = z14.object({
  total: z14.string().nullable(),
  scheduled: z14.string(),
  invoiced: z14.string(),
  paid: z14.string(),
  outstanding: z14.string(),
  /** Signature minus instalments; negative when over-scheduled; null while not priced. */
  unscheduled: z14.string().nullable()
});
var dealDetailResourceSchema = z14.object({
  deal: dealSummaryResourceSchema,
  company: z14.object({ id: z14.string(), name: z14.string(), origin: companyOriginSchema }),
  stageHistory: z14.array(stageChangeResourceSchema),
  contacts: z14.array(contactResourceSchema),
  /** Contacts of the company, to pick from. */
  companyContacts: z14.array(contactResourceSchema),
  activities: z14.array(activityResourceSchema),
  reminders: z14.array(reminderResourceSchema),
  documents: z14.array(dealDocumentResourceSchema),
  /** Newest version first (lot 21). */
  proposals: z14.array(dealProposalResourceSchema),
  installments: z14.array(dealInstallmentResourceSchema),
  signature: signatureSummaryResourceSchema,
  /** Appointments of the deal, earliest first (lot 22). */
  appointments: z14.array(appointmentResourceSchema),
  /** Active meeting kinds, for the booking links. */
  appointmentTypes: z14.array(appointmentTypeResourceSchema)
});
var todoResourceSchema = z14.object({
  open: z14.array(reminderResourceSchema),
  /** Most recently done first, at most 50. */
  done: z14.array(reminderResourceSchema),
  dealsWithoutNextAction: z14.array(
    dealSummaryResourceSchema.extend({
      /** Paris days since the last done reminder, else since the deal's creation. */
      idleDays: z14.number()
    })
  ),
  /** Late and today's reminders (navigation badge). */
  dueCount: z14.number(),
  /** Scheduled appointments not over yet, earliest first (lot 22). */
  appointments: z14.array(appointmentResourceSchema)
});
var directoryCompanyResourceSchema = z14.object({
  siren: z14.string(),
  name: z14.string(),
  legalName: z14.string(),
  nafCode: z14.string().nullable(),
  headcount: z14.string().nullable(),
  address: z14.string().nullable(),
  billingAddress: z14.object({ street: z14.string(), zipCode: z14.string(), city: z14.string(), countryCode: z14.string() }).nullable()
});
var financialsSourceSchema = z14.enum(["inpi", "recherche_entreprises"]);
var companyFinancialsResourceSchema = z14.object({
  /** Without a SIREN nothing is looked up and every other field is empty. */
  siren: z14.string().nullable(),
  /** Most recent published revenue, or null when none is published. */
  revenue: z14.object({
    /** Net revenue, decimal euros (whole euros as filed). */
    amount: z14.string(),
    fiscalYear: z14.number().int(),
    /** Closing date `YYYY-MM-DD`; null when the source only gives the year. */
    fiscalYearEnd: z14.string().nullable(),
    source: financialsSourceSchema
  }).nullable(),
  /** Latest accounts filed at the INPI, published or confidential. */
  latestFiling: z14.object({ fiscalYearEnd: z14.string(), confidential: z14.boolean() }).nullable(),
  /** When the registers were last queried (ISO 8601); null before the first lookup. */
  checkedAt: z14.string().nullable(),
  /** Registers that failed on this call: the figure may be partial or older. */
  failedSources: z14.array(financialsSourceSchema)
});
var convertDealResultSchema = z14.object({
  accounts: z14.array(z14.object({ id: z14.string(), name: z14.string() })),
  chargeId: z14.string().nullable(),
  /** SEPA mandate link of the main account, to send to the billing contact. */
  mandateUrl: z14.string().nullable(),
  /** The billing contact the link is meant for. */
  mandateRecipient: z14.object({ name: z14.string(), email: z14.string() }).nullable()
});
var unlinkedAccountsResourceSchema = z14.object({
  accounts: z14.array(z14.object({ id: z14.string(), name: z14.string(), legalName: z14.string() }))
});

// src/team.ts
import { z as z15 } from "zod";
var roleSchema = z15.enum(["admin", "employee"]);
var permissionSchema = z15.enum(["crm", "operations", "team", "convert_deal", "all_deals"]);
var meResourceSchema = z15.object({
  id: z15.string(),
  email: z15.string().email(),
  supabaseUserId: z15.string(),
  displayName: z15.string().nullable(),
  role: roleSchema,
  /** What the console may show this member. */
  permissions: z15.array(permissionSchema)
});
var teamMemberResourceSchema = z15.object({
  id: z15.string(),
  email: z15.string(),
  displayName: z15.string().nullable(),
  role: roleSchema,
  disabledAt: z15.string().nullable(),
  createdAt: z15.string(),
  /** Open deals carried by the member. */
  openDeals: z15.number()
});
var teamResourceSchema = z15.object({ members: z15.array(teamMemberResourceSchema) });
var inviteTeamMemberSchema = z15.object({
  email: z15.string().trim().email().max(254),
  displayName: z15.string().trim().max(100).nullable().optional(),
  role: roleSchema
});
var inviteTeamMemberResultSchema = z15.object({
  member: teamMemberResourceSchema,
  /** False when the e-mail already had a sign-in account: no e-mail was sent. */
  invited: z15.boolean()
});
var updateTeamMemberSchema = z15.object({
  role: roleSchema,
  displayName: z15.string().trim().max(100).nullable(),
  disabled: z15.boolean()
}).partial();
var reassignDealsSchema = z15.object({ toId: z15.string() });
var reassignDealsResultSchema = z15.object({ deals: z15.number(), reminders: z15.number() });
export {
  ERROR_CODES,
  ERROR_CODE_STATUS,
  PROBLEM_CONTENT_TYPE,
  REQUEST_OUTCOME_DESCRIPTION,
  WEBHOOK_EVENT_TYPES,
  acceptedFileMimeTypeSchema,
  activityResourceSchema,
  adminUsageResourceSchema,
  apiKeyKindSchema,
  apiKeyResourceSchema,
  appointmentResourceSchema,
  appointmentTypeResourceSchema,
  appointmentTypesResourceSchema,
  attachAppointmentSchema,
  attemptDetailResourceSchema,
  billingAddressSchema,
  capChangeActorTypeSchema,
  capChangeResourceSchema,
  capScopeSchema,
  chargeFrequencySchema,
  chargeResourceSchema,
  clientCapResourceSchema,
  clientResourceSchema,
  closeBillingPeriodSchema,
  closeMonthSchema,
  companiesResourceSchema,
  companyAccountResourceSchema,
  companyDetailResourceSchema,
  companyFinancialsResourceSchema,
  companyListItemResourceSchema,
  companyOriginSchema,
  companyStatusSchema,
  contactResourceSchema,
  contactRoleSchema,
  convertDealResultSchema,
  convertDealSchema,
  createAdjustmentSchema,
  createApiKeySchema,
  createAppointmentTypeSchema,
  createChargeSchema,
  createClientBillingSchema,
  createClientSchema,
  createCompanySchema,
  createContactSchema,
  createDealDocumentSchema,
  createDealSchema,
  createExchangeRateSchema,
  createInstallmentSchema,
  createModelSchema,
  createPlatformChargeSchema,
  createPricingRuleSchema,
  createProposalSchema,
  createReminderSchema,
  createRequestBodySchema,
  createRequestBodyShape,
  createTariffSchema,
  createUseCaseSchema,
  createUserSchema,
  createdApiKeyResourceSchema,
  dealDetailResourceSchema,
  dealDocumentCategorySchema,
  dealDocumentResourceSchema,
  dealInstallmentResourceSchema,
  dealModelSchema,
  dealProposalResourceSchema,
  dealStageSchema,
  dealSummaryResourceSchema,
  decideProposalSchema,
  directoryCompanyResourceSchema,
  discoveryReportSchema,
  duplicateUseCaseSchema,
  environmentSchema,
  errorCodeSchema,
  errorTitle,
  euroAmountSchema,
  externalIdSchema,
  fileIdSchema,
  filePartSchema,
  fileResourceSchema,
  followUpFlagSchema,
  imageRequestInputSchema,
  imageResultSchema,
  installmentStatusSchema,
  inviteTeamMemberResultSchema,
  inviteTeamMemberSchema,
  invoiceDescriptionSchema,
  invoiceNameSchema,
  linkCompanyAccountSchema,
  linkQontoClientSchema,
  logActivitySchema,
  loggedActivityKindSchema,
  lostReasonSchema,
  markDealLostSchema,
  meResourceSchema,
  modelFeatureSchema,
  moneySchema,
  moveDealStageSchema,
  nextActionResourceSchema,
  openStageSchema,
  outcomeCodeSchema,
  parsedStripeEventSchema,
  permissionSchema,
  pipelineResourceSchema,
  pipelineStatsResourceSchema,
  plannedInstallmentSchema,
  platformChargeFrequencySchema,
  platformChargeResourceSchema,
  pricingRuleBodySchema,
  pricingUnitSchema,
  problemDocumentSchema,
  proposalStatusSchema,
  providerSchema,
  qontoClientResourceSchema,
  reassignDealsResultSchema,
  reassignDealsSchema,
  reminderResourceSchema,
  reminderTimingSchema,
  reminderTypeSchema,
  requestDetailResourceSchema,
  requestFileResourceSchema,
  requestIdSchema,
  requestInputSchema,
  requestListSchema,
  requestOutcomeShape,
  requestResourceSchema,
  requestResultSchema,
  requestStatusSchema,
  roleSchema,
  sendProposalSchema,
  setBillingDeferralSchema,
  setMyCapResponseSchema,
  setMyCapSchema,
  setWebhookSchema,
  signatureSummaryResourceSchema,
  sirenSchema,
  smsRequestInputSchema,
  smsResultSchema,
  smsSettingsSchema,
  stageChangeResourceSchema,
  stripeWebhookEventSchema,
  systemEventResourceSchema,
  tariffCurrencySchema,
  teamMemberResourceSchema,
  teamResourceSchema,
  textPartSchema,
  textRequestInputSchema,
  textResultSchema,
  timestampSchema,
  todoResourceSchema,
  unlinkedAccountsResourceSchema,
  unmatchedAppointmentsResourceSchema,
  updateActivitySchema,
  updateAppointmentTypeSchema,
  updateChargeSchema,
  updateClientCapSchema,
  updateClientSchema,
  updateCompanySchema,
  updateContactSchema,
  updateDealSchema,
  updateInstallmentSchema,
  updateModelSchema,
  updatePlatformChargeSchema,
  updateProposalSchema,
  updateReminderSchema,
  updateTeamMemberSchema,
  updateUseCaseSchema,
  updateUserSchema,
  uploadDealDocumentFieldsSchema,
  usageMeSchema,
  usageMeWithAmountsSchema,
  usageMeWithoutAmountsSchema,
  useCaseCategorySchema,
  useCaseIdSchema,
  useCaseKindSchema,
  userListSchema,
  userResourceSchema,
  webhookDeliveryIdSchema,
  webhookDeliveryResourceSchema,
  webhookEventSchema,
  webhookEventTypeSchema,
  webhookRequestCancelledSchema,
  webhookRequestExpiredSchema,
  webhookRequestFailedSchema,
  webhookRequestPausedSchema,
  webhookRequestResumedSchema,
  webhookRequestSucceededSchema
};
//# sourceMappingURL=index.js.map